import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

import { useNavigate } from "react-router-dom";

import { useSettings } from "../../contexts/SettingsContext";
import { useTheme } from "../../contexts/ThemeContext";

import { getCid, getMetadata } from "../../utils/encryptedStorage";

import aiAutomationApi from "../../utils/aiAutomationApi";

import coreApi from "../../utils/coreApi";
import { handleMailProviderReconnect, isMailProviderAuthError } from "../../utils/gmailOAuth";
import { getFeatureBlockedMessage, useCompanyFeatureAccess } from "../../utils/companyFeatureAccess";

import AppHeader from "../../components/AppHeader";

import ChatMain from "../../pages/chat/ChatMain";
import { RESUME_LIBRARY_LABEL, VIEW_RESUME_LIBRARY_LABEL, getResumeStatusLabel } from "../../utils/uxLabels";

import DashboardResumeUploadPanel from "./DashboardResumeUploadPanel";

/* ─────────────────────────────────────────────────────────────

   COLOR TOKENS  (Doc-2 palette, theme-aware primary overlay)

───────────────────────────────────────────────────────────── */

const C = {

  blue:    "var(--dashboard-blue, #3B5BDB)", blueLt:  "var(--dashboard-blue-soft, #EEF2FF)", blueMid: "var(--dashboard-blue-border, #BFCBFF)",

  green:   "var(--dashboard-green, #059669)", greenLt: "var(--dashboard-green-soft, #ECFDF5)",

  amber:   "var(--dashboard-amber, #D97706)", amberLt: "var(--dashboard-amber-soft, #FFFBEB)",

  red:     "var(--dashboard-red, #DC2626)", redLt:   "var(--dashboard-red-soft, #FEF2F2)",

  bg:      "var(--dashboard-bg, #F8FAFC)", border:  "var(--dashboard-border, #E2E8F0)", surface: "var(--dashboard-surface, #F8FAFC)",

  muted:   "var(--dashboard-muted, #64748B)",    text:    "var(--dashboard-text, #0F172A)",

};


/* ── PIN ICON (lollipop map pin) ── */
function PinIcon({ color = "#5F5E5A" }) {
  return (
    <svg width="9" height="11" viewBox="0 0 10 13" fill={color} style={{ flexShrink: 0, marginTop: 1 }}>
      <path d="M5 0C2.79 0 1 1.79 1 4c0 3 4 9 4 9s4-6 4-9C9 1.79 7.21 0 5 0zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
    </svg>
  );
}

/* ── META PILL (location / source / time chips) ── */
const PILL_STYLES = {
  loc:    { bg: "var(--dashboard-surface, #F8FAFC)", color: "var(--dashboard-muted, #475569)", border: "var(--dashboard-border, #E2E8F0)" },
  src:    { bg: "var(--dashboard-blue-soft, #EEF2FF)", color: "var(--dashboard-blue, #3B5BDB)", border: "var(--dashboard-blue-border, #BFCBFF)" },
  naukri: { bg: "var(--dashboard-amber-soft, #FFFBEB)", color: "var(--dashboard-amber, #B45309)", border: "var(--dashboard-amber-border, #FDE68A)" },
  time:   { bg: "var(--dashboard-blue-soft, #EEF2FF)", color: "var(--dashboard-blue, #3B5BDB)", border: "var(--dashboard-blue-border, #BFCBFF)" },
  alert:  { bg: "var(--dashboard-red-soft, #FCEBEB)", color: "var(--dashboard-red, #A32D2D)", border: "var(--dashboard-red-border, #F7C1C1)" },
  exp:    { bg: "var(--dashboard-green-soft, #ECFDF5)", color: "var(--dashboard-green, #047857)", border: "var(--dashboard-green-border, #A7F3D0)" },
};

function MetaPill({ type = "loc", children, icon }) {
  const s = PILL_STYLES[type] || PILL_STYLES.loc;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontSize: 10, fontWeight: 500, padding: "1px 7px",
      borderRadius: 20, whiteSpace: "nowrap",
      background: s.bg, color: s.color, border: `0.5px solid ${s.border}`,
    }}>
      {icon}{children}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────

   PANEL IDS

───────────────────────────────────────────────────────────── */

const P = {

  RESUME_UPLOAD:      "resume_upload",

  MASTER_DATABASE:    "master_database",

  MY_INTERVIEWS:      "my_interviews",

  MEETING_NOTES:      "meeting_notes",

  RESCHEDULE:         "reschedule",

  NOTIFICATIONS:      "notifications",

  SHOW_CANDIDATES:    "show_candidates",

  TODAY_TASKS:        "today_tasks",

  MANAGE_CAMPAIGN:    "manage_campaign",

  CANDIDATE_PROFILE:  "candidate_profile",

  CHAT:               "chat",

};

const queueDashboardInitialQuery = (sessionId, query, options = {}) => {
  if (!sessionId || !String(query || '').trim()) return;
  try {
    window.sessionStorage.setItem(
      'dashboard_pending_session_query',
      JSON.stringify({
        sessionId,
        query: String(query).trim(),
        token: `${sessionId}:${Date.now()}`,
        localOnly: options.localOnly === true,
      })
    );
  } catch (error) {
    console.warn('[DashboardPage] Failed to queue dashboard initial query:', error);
  }
};



const PANEL_META = {

  [P.RESUME_UPLOAD]:      { title: "Candidate Library",    sub: "Import, analyze, and organize resumes", icon: "📄", color: "#3B5BDB", bg: "#EEF2FF" },

  [P.MASTER_DATABASE]:    { title: "Master Database",  sub: "All candidates · master list", icon: "🗄️", color: "#0a7a4a", bg: "#e6f9f0" },

  [P.MY_INTERVIEWS]:      { title: "My Interviews",    sub: "View all your interviews", icon: "📅", color: "#8a5c00", bg: "#fff8e6" },

  [P.MEETING_NOTES]:      { title: "Meeting Notes",    sub: "AI notes & transcript", icon: "📝", color: "#8a5c00", bg: "#fff8e6" },

  [P.RESCHEDULE]:         { title: "Reschedule",       sub: "Pending reschedule requests", icon: "🔄", color: "#a32d2d", bg: "#fde8e8" },

  [P.NOTIFICATIONS]:      { title: "Notifications",    sub: "Recent notifications", icon: "🔔", color: "#B45309", bg: "#FFF4E5" },

  [P.SHOW_CANDIDATES]:    { title: "Show Candidates",  sub: "View candidates by session", icon: "👥", color: "#3B5BDB", bg: "#EEF2FF" },

  [P.TODAY_TASKS]:        { title: "Today's Tasks",    sub: "Follow-ups & actions", icon: "✅", color: "#0a7a4a", bg: "#e6f9f0" },

  [P.MANAGE_CAMPAIGN]:    { title: "Manage Campaign",  sub: "AI-powered campaign", icon: "🚀", color: "#28ACA8", bg: "#E8FBFA" },

  [P.CANDIDATE_PROFILE]:  { title: "Candidate Profile",sub: "Search candidates", icon: "🔍", color: "#3B5BDB", bg: "#EEF2FF" },

  [P.CHAT]:               { title: "Pipeline Overview", sub: "Session chat & outreach", icon: "💬", color: "#28ACA8", bg: "#E8FBFA" },

};



const QUICK_ACTIONS = [

  { id: 'create_jd', label: "Create JD" },

  { id: P.RESUME_UPLOAD,      label: "+ Resume Upload" },

  { id: P.MASTER_DATABASE,    label: "Master Database" },

  { id: P.MANAGE_CAMPAIGN,    label: "Manage Campaign" },

  { id: P.SHOW_CANDIDATES,    label: "Show Candidates" },

  { id: P.TODAY_TASKS,        label: "Today's Tasks" },

];

const PANEL_FEATURE_ACCESS = {
  [P.RESUME_UPLOAD]: "resume_upload",
  [P.MY_INTERVIEWS]: "scheduling",
  [P.MEETING_NOTES]: "manual_interview",
  [P.RESCHEDULE]: "scheduling",
  [P.MANAGE_CAMPAIGN]: "sourcing",
};



// Session Popup for Create JD - Simple Modal with Conversational Text

function SessionPopup({ open, onClose, onSelect }) {
  const navigate = useNavigate();
  const jdFileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [title, setTitle] = useState('');
  const quickPrompts = [
    'Python Developer, Chennai, 2 years',
    'UI/UX Designer, Bangalore, 3 years',
    'Sales Manager, Mumbai, 5 years',
  ];

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setLoading(true);
    const m = getMetadata();
    try {
      const sessionId = `session_${Date.now()}`;
      
      const payload = {
        session_id: sessionId,
        cid: String(m?.cid || ''),
        user_id: String(m?.uid || m?.userId || m?.id || ''),
        status: 'active',
      };
      const res = await aiAutomationApi.post('/sessions', payload);
      const newSessionId = String(res?.session_id || sessionId);
      queueDashboardInitialQuery(newSessionId, title.trim());
      aiAutomationApi.patch(`/sessions/${newSessionId}/title`, {
        title: title.trim().slice(0, 80),
      }).catch(() => {});
      navigate(`/chat?session_id=${newSessionId}&from_dashboard=true`);
    } catch (err) {
      console.error('Failed to create session:', err);
      setLoading(false);
    }
  };

  const createSessionAndOpenChat = async (query, titleHint = '') => {
    const m = getMetadata();
    const sessionId = `session_${Date.now()}`;
    const payload = {
      session_id: sessionId,
      cid: String(m?.cid || ''),
      user_id: String(m?.uid || m?.userId || m?.id || ''),
      status: 'active',
    };
    const res = await aiAutomationApi.post('/sessions', payload);
    const newSessionId = String(res?.session_id || sessionId);
    queueDashboardInitialQuery(newSessionId, query);
    const safeTitle = String(titleHint || query || 'Uploaded JD').replace(/\s+/g, ' ').trim().slice(0, 80);
    aiAutomationApi.patch(`/sessions/${newSessionId}/title`, {
      title: safeTitle || 'Uploaded JD',
    }).catch(() => {});
    navigate(`/chat?session_id=${newSessionId}&from_dashboard=true`);
  };

  const openJdFilePicker = () => {
    if (loading || uploading) return;
    setUploadError('');
    if (jdFileInputRef.current) {
      jdFileInputRef.current.value = '';
      jdFileInputRef.current.click();
    }
  };

  const handleJdFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await aiAutomationApi.postForm('/jd/upload-document', formData);
      if (!res?.success || !String(res?.jd_text || '').trim()) {
        throw new Error(res?.detail || 'Could not extract JD text from this file.');
      }

      let enrichedJd = String(res.jd_text || '').trim();
      const missingFields = Array.isArray(res.missing_fields) ? res.missing_fields : [];
      if (missingFields.length > 0) {
        const missingLabels = missingFields.map((field) => field?.label || field?.key).filter(Boolean);
        enrichedJd += `\n\n--- Missing Details To Ask User ---\n${missingLabels.join(', ')}`;
      }

      await createSessionAndOpenChat(
        `Create a JD from this document:\n\n${enrichedJd}`,
        file.name || 'Uploaded JD'
      );
    } catch (err) {
      console.error('Failed to upload JD from dashboard:', err);
      setUploadError(err?.message || 'Upload failed. Please try again.');
      setUploading(false);
    } finally {
      if (jdFileInputRef.current) jdFileInputRef.current.value = '';
    }
  };

  if (!open) return null;

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(15,23,42,0.38)',backdropFilter:'blur(6px)',WebkitBackdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',padding:'24px'}}>
      <div style={{
        background:'var(--dashboard-modal-bg, var(--dashboard-card, #fff))',
        borderRadius:24,
        width:'100%',
        maxWidth:560,
        padding:'28px',
        border:'1px solid var(--dashboard-modal-border, var(--dashboard-border, #E2E8F0))',
        boxShadow:'var(--dashboard-modal-shadow, 0 24px 60px rgba(15,23,42,0.18))'
      }}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,marginBottom:24}}>
          <div style={{display:'flex',gap:14,alignItems:'flex-start'}}>
            <div style={{
              width:48,
              height:48,
              borderRadius:16,
              background:'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)',
              color:'#fff',
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              boxShadow:'0 12px 24px rgba(37,99,235,0.22)',
              flexShrink:0
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20h9" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </div>
            <div>
              <div style={{display:'inline-flex',alignItems:'center',padding:'5px 10px',borderRadius:999,background:C.blueLt,color:C.blue,fontSize:11,fontWeight:700,letterSpacing:'0.02em',textTransform:'uppercase',marginBottom:10}}>
                Create JD
              </div>
              <h3 style={{fontSize:18,fontWeight:700,color:'var(--dashboard-text, #0F172A)',margin:'0 0 8px 0',lineHeight:1.35}}>
                Start a new job description
              </h3>
              <p style={{fontSize:13,color:'var(--dashboard-muted, #475569)',margin:'0 0 6px 0',lineHeight:1.6}}>
                Enter the role with a physical location and experience level. I’ll use that to open a guided JD creation flow.
              </p>
              <p style={{fontSize:12,color:'var(--dashboard-muted, #94A3B8)',margin:0,lineHeight:1.6}}>
                Include city, state, or country. Example: Chennai, Bangalore, Mumbai, New York.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
            style={{
              width:36,
              height:36,
              borderRadius:12,
              border:'1px solid var(--dashboard-border, #E2E8F0)',
              background:'var(--dashboard-input-bg, var(--dashboard-surface, #FFFFFF))',
              color:'var(--dashboard-muted, #64748B)',
              cursor:loading ? 'default' : 'pointer',
              display:'flex',
              alignItems:'center',
              justifyContent:'center',
              boxShadow:'0 8px 18px rgba(15,23,42,0.05)',
              flexShrink:0
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div style={{marginBottom:18}}>
          <input
            ref={jdFileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            style={{ display: 'none' }}
            onChange={handleJdFileSelected}
          />
          <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--dashboard-text, #334155)',marginBottom:8,letterSpacing:'0.02em',textTransform:'uppercase'}}>
            Role Prompt
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g., Python Developer, Chennai, 2 years"
            style={{
              width:'100%',
              padding:'16px 18px',
              borderRadius:18,
              border:'1px solid var(--dashboard-border, #CBD5E1)',
              background:'var(--dashboard-surface, #FFFFFF)',
              fontSize:16,
              color:'var(--dashboard-text, #0F172A)',
              outline:'none',
              boxSizing:'border-box',
              boxShadow:'var(--dashboard-input-shadow, inset 0 1px 2px rgba(15,23,42,0.04))'
            }}
            autoFocus
            onKeyDown={e => {if(e.key==='Enter')handleSubmit()}}
          />
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,marginTop:10,flexWrap:'wrap'}}>
            <p style={{fontSize:12,color:'var(--dashboard-muted, #64748B)',margin:0}}>
              Format: role, city, experience
            </p>
            <p style={{fontSize:12,color:'var(--dashboard-muted, #94A3B8)',margin:0}}>
              Example: Senior Recruiter, Bangalore, 4 years
            </p>
          </div>
          {uploadError && (
            <div style={{
              marginTop:12,
              padding:'10px 12px',
              borderRadius:12,
              border:'1px solid var(--dashboard-red-border, #FECACA)',
              background:'var(--dashboard-red-soft, #FEF2F2)',
              color:'var(--dashboard-red, #B91C1C)',
              fontSize:12,
              fontWeight:600,
            }}>
              {uploadError}
            </div>
          )}
        </div>

        <div style={{marginBottom:24}}>
          <div style={{fontSize:12,fontWeight:700,color:'var(--dashboard-muted, #475569)',marginBottom:10}}>
            Quick examples
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
            {quickPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => setTitle(prompt)}
                style={{
                  padding:'9px 12px',
                  borderRadius:999,
                  border:'1px solid var(--dashboard-blue-border, #DBEAFE)',
                  background:'var(--dashboard-hover, #F8FBFF)',
                  color:C.blue,
                  fontSize:12,
                  fontWeight:600,
                  cursor:'pointer',
                  boxShadow:'0 6px 14px rgba(15,23,42,0.04)'
                }}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        <div style={{display:'flex',gap:8,justifyContent:'space-between',alignItems:'center',flexWrap:'wrap'}}>
          <button
            type="button"
            onClick={openJdFilePicker}
            disabled={loading || uploading}
            style={{
              padding:'12px 16px',
              borderRadius:14,
              border:'1px solid var(--dashboard-blue-border, #BFCBFF)',
              background:'var(--dashboard-blue-soft, #EEF2FF)',
              color:C.blue,
              fontSize:14,
              fontWeight:700,
              cursor:loading || uploading ? 'default' : 'pointer',
              display:'inline-flex',
              alignItems:'center',
              gap:8,
            }}
          >
            {uploading ? (
              <span style={{width:14,height:14,borderRadius:'50%',border:'2px solid currentColor',borderTopColor:'transparent',display:'inline-block',animation:'spin 0.8s linear infinite'}} />
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 9l5-5 5 5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 16v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4" />
              </svg>
            )}
            {uploading ? 'Uploading...' : 'Upload JD'}
          </button>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <button
            onClick={onClose}
            disabled={loading || uploading}
            style={{
              padding:'12px 18px',
              borderRadius:14,
              border:'1px solid var(--dashboard-border, #CBD5E1)',
              background:'var(--dashboard-card, #fff)',
              color:'var(--dashboard-text, #374151)',
              fontSize:14,
              fontWeight:600,
              cursor:loading || uploading ? 'default' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || loading || uploading}
            style={{
              padding:'12px 18px',
              borderRadius:14,
              border:'none',
              background:!title.trim() || loading || uploading ? 'var(--dashboard-disabled-bg, #94A3B8)' : 'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)',
              color:'#fff',
              fontSize:14,
              fontWeight:700,
              cursor:!title.trim() || loading || uploading ? 'default' : 'pointer',
              boxShadow:!title.trim() || loading || uploading ? 'none' : '0 12px 24px rgba(37,99,235,0.18)'
            }}
          >
            {loading ? 'Creating...' : 'Create JD'}
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}



/* ─────────────────────────────────────────────────────────────

   USER INFO

───────────────────────────────────────────────────────────── */

function getUserInfo() {

  try {

    const m = getMetadata();

    if (m) return { id: m.uid || m.userId || m.id || "user", cid: m.cid || null, name: m.name || "User", email: m.email || "", role: m.role || "Recruiter" };

    const raw = localStorage.getItem("user");

    if (raw) { const u = JSON.parse(raw); return { id: u.id || "user", cid: u.cid || null, name: u.name || "User", email: u.email || "", role: u.role || "Recruiter" }; }

  } catch { }

  return { id: "user", cid: null, name: "User", email: "", role: "Recruiter" };

}



const isIdLikeTitle = v => /^session[_\-\s]/i.test(v) || /^session_\d+/i.test(v);

const resolveSessionTitle = s => {

  const cands = [s?.title, s?.custom_title, s?.initial_query, s?.query, s?.job_title]

    .map(v => String(v || "").trim()).filter(Boolean);

  return cands.find(t => !isIdLikeTitle(t)) || "New Chat";

};



const fmtDate = value => {

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return value || "";

  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

};



/* ─────────────────────────────────────────────────────────────

   SHARED UI ATOMS

───────────────────────────────────────────────────────────── */

const avatarColors = ["#3B5BDB","#0a7a4a","#8a5c00","#534AB7","#993556","#185FA5","#3B6D11","#854F0B","#A32D2D","#0F6E56"];



function Avatar({ name, size = 32, colorIdx = 0 }) {

  const initials = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (

    <div style={{

      width: size, height: size, borderRadius: "50%", flexShrink: 0,

      background: avatarColors[colorIdx % avatarColors.length],

      color: "#fff", fontSize: size * 0.34, fontWeight: 600,

      display: "flex", alignItems: "center", justifyContent: "center",

    }}>{initials}</div>

  );

}



const BADGE_STYLES = {

  active:      { bg: C.greenLt, color: C.green,  border: "#b0ecd4" },

  approved:    { bg: C.greenLt, color: C.green,  border: "#b0ecd4" },

  pending:     { bg: C.amberLt, color: C.amber,  border: "#fce0a0" },

  shortlisted: { bg: C.blueLt,  color: C.blue,   border: C.blueMid },

  hired:       { bg: C.surface, color: C.muted, border: C.border },

  scheduled:   { bg: C.blueLt,  color: C.blue,   border: C.blueMid },

  completed:   { bg: C.surface, color: C.muted, border: C.border },

  queued:      { bg: C.surface, color: C.muted, border: C.border },

  rejected:    { bg: C.redLt,   color: C.red,    border: "#fca5a5" },

};

function Badge({ status = "active" }) {

  const s = BADGE_STYLES[(status || "active").toLowerCase()] || BADGE_STYLES.hired;

  return (

    <span style={{

      display: "inline-block", padding: "2px 8px", borderRadius: 8,

      fontSize: 10, fontWeight: 600, letterSpacing: ".2px",

      background: s.bg, color: s.color, border: `1px solid ${s.border}`,

      whiteSpace: "nowrap",

    }}>{status}</span>

  );

}



function Loader() {

  return (

    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "40px 16px", color: C.muted, fontSize: 12 }}>

      <svg style={{ animation: "spin 1s linear infinite", width: 16, height: 16 }} viewBox="0 0 24 24" fill="none">

        <circle cx="12" cy="12" r="10" stroke={C.blue} strokeWidth="3" strokeDasharray="31 63" />

      </svg>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      Loading…

    </div>

  );

}



function Empty({ text }) {

  return (

    <div style={{ padding: "48px 20px", textAlign: "center" }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: C.surface, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
        </svg>
      </div>
      <p style={{ fontSize: 13, fontWeight: 500, color: C.text, margin: 0 }}>{text}</p>
    </div>

  );

}

function CandidateSkeleton() {
  return (
    <div style={{ padding: "12px 14px", marginBottom: 8, borderRadius: 12, border: `1px solid ${C.border}`, background: "var(--dashboard-card, #fff)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, animation: "pulse 1.5s ease-in-out infinite" }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ height: 14, width: "60%", background: C.surface, borderRadius: 4, marginBottom: 8, animation: "pulse 1.5s ease-in-out infinite" }} />
          <div style={{ height: 20, width: "40%", background: C.surface, borderRadius: 10, animation: "pulse 1.5s ease-in-out infinite 0.2s" }} />
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

function PanelSearch({ value, onChange, placeholder = "Search..." }) {
  return (
    <div style={{ padding: "10px 14px", borderBottom: `1px solid ${C.border}`, position: "relative" }}>
      <svg style={{ position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)" }} width="13" height="13" viewBox="0 0 16 16" fill="none">
        <circle cx="7" cy="7" r="5" stroke={C.muted} strokeWidth="1.3"/>
        <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{
        width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20,
        padding: "7px 14px 7px 30px", fontSize: 12, outline: "none", color: C.text, boxSizing: "border-box",
      }}/>
    </div>
  );
}



function ListRow({ name, sub, status, colorIdx = 0, active, onClick }) {

  const [hov, setHov] = useState(false);

  return (

    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{

      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer",

      borderBottom: `1px solid ${C.border}`, background: active ? C.surface : hov ? "var(--dashboard-hover, #f7f8ff)" : "var(--dashboard-card, #fff)",

      transition: "background .1s",

    }}>

      <Avatar name={name} size={34} colorIdx={colorIdx} />

      <div style={{ flex: 1, minWidth: 0 }}>

        <div style={{ fontSize: 12, fontWeight: 500, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>

        <div style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>

      </div>

      {status && <Badge status={status} />}

    </div>

  );

}



/* ─────────────────────────────────────────────────────────────

   STATS BAR  (Doc-2 design + real data)

───────────────────────────────────────────────────────────── */

function StatBtn({ label, value, gradient, icon, badge, onClick }) {

  const [hov, setHov] = useState(false);

  return (

    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{

      display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", cursor: "pointer",

      border: `1px solid ${hov ? C.blueMid : C.border}`, background: hov ? "var(--dashboard-hover, #EEF2FF)" : "var(--dashboard-card, #FFFFFF)",

      borderRadius: 16, transition: "background .12s, transform .12s", flexShrink: 0,
      boxShadow: hov ? "0 10px 20px rgba(37, 99, 235, 0.08)" : "0 6px 16px rgba(15, 23, 42, 0.04)",

    }}>

      <div style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 18px rgba(15,23,42,0.18)" }}>

        {icon}

      </div>

      <div style={{ textAlign: "left" }}>

        <p style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: C.muted, lineHeight: 1, marginBottom: 2, margin: "0 0 2px" }}>{label}</p>

        <p style={{ fontSize: 20, fontWeight: 700, lineHeight: 1, margin: 0, color: C.text }}>{value}</p>

      </div>

      {badge > 0 && <span style={{ background: C.redLt, color: C.red, borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, marginLeft: "auto" }}>{badge}</span>}

    </button>

  );

}



function StickyStatsBar({ stats, onAction, onDelete }) {

  return (

    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: `1px solid ${C.border}`, background: "var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%))", flexShrink: 0, overflowX: "auto" }}>

      <StatBtn label="Resumes" value={stats.resumes} gradient="linear-gradient(135deg,#3B5BDB,#28ACA8)" onClick={() => onAction(P.RESUME_UPLOAD)}

        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>} />

      <StatBtn label="Interviews" value={stats.interviews} gradient="linear-gradient(135deg,#B45309,#D97706)" onClick={() => onAction(P.MY_INTERVIEWS)}

        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3M4 11h16M6 5h12a2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z"/></svg>} />

      <StatBtn label="Master DB" value={stats.masterDb} gradient="linear-gradient(135deg,#3B5BDB,#28ACA8)" onClick={() => onAction(P.MASTER_DATABASE)}

        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"/></svg>} />

      <StatBtn label="Reschedule" value={stats.reschedules} badge={stats.reschedules} gradient="linear-gradient(135deg,#C2410C,#DC2626)" onClick={() => onAction(P.RESCHEDULE)}

        icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6M20 10A8 8 0 005.6 6.2M4 14a8 8 0 0014.4 3.8"/></svg>} />

      {stats.notifications > 0 && (

        <StatBtn label="Alerts" value={stats.notifications} gradient="linear-gradient(135deg,#0284C7,#0EA5E9)" onClick={() => onAction(P.NOTIFICATIONS)}

          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.4a2 2 0 01-.6-1.4V11a6 6 0 10-12 0v3.2c0 .53-.21 1.04-.59 1.41L4 17h5m2 0v1a2 2 0 104 0v-1m-4 0h4"/></svg>} />

      )}
      
      {stats.campaigns > 0 && (

        <StatBtn label="Campaigns" value={stats.campaigns} gradient="linear-gradient(135deg,#28ACA8,#0891B2)" onClick={() => onAction(P.MANAGE_CAMPAIGN)}

          icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>} />

      )}

      <button onClick={onDelete} style={{

        display:"flex", alignItems:"center", padding:"8px", cursor:"pointer",

        background:"var(--dashboard-card, #FFFFFF)", border:`1px solid ${C.border}`, borderRadius:16, transition:"background .12s", flexShrink:0, marginLeft:"auto", boxShadow:"0 6px 16px rgba(15, 23, 42, 0.04)",

      }}>

        <div style={{ width:28, height:28, borderRadius:8, flexShrink:0, background:"linear-gradient(135deg,#DC2626,#EF4444)", display:"flex", alignItems:"center", justifyContent:"center" }}>

          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>

        </div>

      </button>

    </div>

  );

}



/* ─────────────────────────────────────────────────────────────

   ONBOARDING CARD  (smart, stats-aware)

───────────────────────────────────────────────────────────── */

function OnboardingCard({ stats, onAction, user }) {

  const hasResumes   = stats.resumes > 0;

  const hasJDs       = stats.jds > 0;

  const hasCampaigns = stats.campaigns > 0;

  const firstName  = (user?.name || "there").split(" ")[0];



  let icon, title, subtitle, description, steps;



  if (!hasResumes && !hasJDs) {

    icon="👋"; title=`Welcome, ${firstName}!`; subtitle="Let's get your hiring process started.";

    description="Begin by uploading resumes to build your talent pool, or start a campaign to source candidates from LinkedIn.";

    steps=[{ icon:"📄", label:"Upload Resume",   action:P.RESUME_UPLOAD,   primary:true },

           { icon:"🚀", label:"Campaign",  action:P.MANAGE_CAMPAIGN,  primary:false }];

  } else if (hasResumes && !hasJDs) {

    icon="✅"; title="Resumes ready!"; subtitle=`You have ${stats.resumes} resume${stats.resumes!==1?"s":""} uploaded.`;

    description="Explore your candidate database or upload more resumes.";

    steps=[{ icon:"📄", label:VIEW_RESUME_LIBRARY_LABEL, action:P.RESUME_UPLOAD, primary:true },

           { icon:"🗄️", label:"View Candidates",      action:P.MASTER_DATABASE,  primary:false },

           { icon:"📄", label:"Upload More Resumes",   action:P.RESUME_UPLOAD,    primary:false },

           ...(hasCampaigns ? [{ icon:"🚀", label:"Manage Campaign", action:P.MANAGE_CAMPAIGN, primary:false }] : []),

           ];

  } else if (!hasResumes && hasJDs) {

    icon="💼"; title="Job description ready!"; subtitle=`You have ${stats.jds} active job description${stats.jds!==1?"s":""}.`;

    description="Upload resumes to start matching candidates, or jump into your campaign to source from LinkedIn.";

    steps=[{ icon:"📄", label:"Upload Resumes",   action:P.RESUME_UPLOAD,    primary:true },

           { icon:"🚀", label:"Manage Campaign",  action:P.MANAGE_CAMPAIGN,  primary:false }];

  } else {

    icon="🎯"; title="You're all set!"; subtitle=`${stats.resumes} resumes · ${stats.interviews} interviews · ${stats.jds} JDs`;

    description="Your hiring pipeline is active. Here's what needs your attention today:";

    steps=[

      { icon:"👥", label:"Show Candidates",    action:P.SHOW_CANDIDATES,  primary:true },

      { icon:"✅", label:"Today's Tasks",      action:P.TODAY_TASKS,      primary:false },

      { icon:"🗄️", label:"Master Database",    action:P.MASTER_DATABASE,  primary:false },

      { icon:"🔄", label:"Reschedule Requests",action:P.RESCHEDULE,       primary:false },

      { icon:"📅", label:"My Interviews",      action:P.MY_INTERVIEWS,    primary:false },

      ...(hasCampaigns ? [{ icon:"🚀", label:"Manage Campaign", action:P.MANAGE_CAMPAIGN, primary:false }] : []),

    ];

  }



  return (

    <div style={{ borderRadius:18, overflow:"hidden", border:`1px solid ${C.border}`, background:"var(--dashboard-card, #FFFFFF)", boxShadow:"0 14px 30px rgba(15,23,42,0.05)" }}>

      <div style={{ background:"var(--dashboard-onboarding-header, linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%))", padding:"14px 18px", display:"flex", alignItems:"center", gap:10, borderBottom:`1px solid ${C.border}` }}>

        <div style={{ width:32, height:32, borderRadius:"50%", background:C.blue, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, boxShadow:"0 8px 16px rgba(37, 99, 235, 0.14)" }}>{icon}</div>

        <div>

          <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0 }}>{title}</p>

          <p style={{ fontSize:11, color:C.muted, margin:"2px 0 0" }}>{subtitle}</p>

        </div>

      </div>

      <div style={{ padding:"14px 16px" }}>

        <p style={{ fontSize:13, color:C.muted, lineHeight:1.65, marginBottom:12 }}>{description}</p>

        <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>

          {steps.map(s => (

            <button key={s.action} onClick={() => onAction(s.action)} style={{

              display:"flex", alignItems:"center", gap:6, borderRadius:10,

              padding:"7px 14px", fontSize:12, fontWeight:500, cursor:"pointer",

              background:s.primary?"linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)":"var(--dashboard-card, #FFFFFF)", color:s.primary?"#fff":C.text,
              boxShadow:s.primary?"0 10px 20px rgba(37, 99, 235, 0.16)":"0 8px 16px rgba(15,23,42,0.04)",
              border:s.primary?"none":`1px solid ${C.border}`,

            }}>

              <span>{s.icon}</span>{s.label}

            </button>

          ))}

        </div>

      </div>

    </div>

  );

}



/* ─────────────────────────────────────────────────────────────

   CHAT BUBBLES

───────────────────────────────────────────────────────────── */

function fmt(d) {

  if (!d) return "";

  const t = new Date(d), h = t.getHours(), m = t.getMinutes();

  return `${h%12||12}:${String(m).padStart(2,"0")} ${h>=12?"PM":"AM"}`;

}

function SystemAvatar({ size = 28 }) {

  return (

    <div style={{

      width:size, height:size, borderRadius:size * 0.42,

      background:"linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)",

      color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",

      flexShrink:0, boxShadow:"0 10px 22px rgba(59, 91, 219, 0.18)",

      position:"relative", overflow:"hidden",

    }}>

      <div style={{ position:"absolute", inset:0, background:"linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.02) 60%)" }} />

      <svg width={size * 0.58} height={size * 0.58} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" style={{ position:"relative", zIndex:1 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l1.7 3.45L17.5 8.2l-2.75 2.68.65 3.82L12 12.9l-3.4 1.8.65-3.82L6.5 8.2l3.8-1.75L12 3z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.2v.01" />
      </svg>

      <style>{`
        @keyframes tgptSystemGlow {
          0%, 100% { transform: translateY(0); box-shadow: 0 10px 22px rgba(59, 91, 219, 0.18); }
          50% { transform: translateY(-1px); box-shadow: 0 14px 28px rgba(40, 172, 168, 0.20); }
        }
      `}</style>

    </div>

  );

}



function BotBubble({ text, actions=[], onAction, ts }) {

  return (

    <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>

      <div style={{ marginTop:1, animation:"tgptSystemGlow 2.4s ease-in-out infinite" }}>

        <SystemAvatar />

      </div>

      <div>

        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:"8px 14px 14px 14px", padding:"10px 13px", fontSize:13, color:C.text, lineHeight:1.6, maxWidth:460 }}>

          {text}

          {actions.length > 0 && (

            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>

              {actions.map((a,i) => (

                <button key={i} onClick={() => onAction(a.action)} style={{

                  display:"inline-flex", alignItems:"center", gap:5, background:"var(--dashboard-card, #fff)", color:C.blue,

                  border:`1px solid ${C.blueMid}`, borderRadius:16, padding:"5px 12px", fontSize:12, fontWeight:500, cursor:"pointer",

                }}><span>{a.icon}</span>{a.label}</button>

              ))}

            </div>

          )}

        </div>

        {ts && <div style={{ fontSize:10, color:"#ccc", marginTop:4 }}>{fmt(ts)}</div>}

      </div>

    </div>

  );

}



function UserBubble({ text, ts, initials="U" }) {

  return (

    <div style={{ display:"flex", flexDirection:"row-reverse", gap:8, alignItems:"flex-end" }}>

      <div style={{ width:28, height:28, borderRadius:"50%", background:C.blueLt, color:C.blue, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{initials}</div>

      <div style={{ alignItems:"flex-end", display:"flex", flexDirection:"column", gap:3 }}>

        <div style={{ background:C.blue, color:"#fff", borderRadius:"14px 8px 14px 14px", padding:"9px 13px", fontSize:13, lineHeight:1.55, maxWidth:340 }}>{text}</div>

        {ts && <div style={{ fontSize:10, color:"#ccc" }}>{fmt(ts)}</div>}

      </div>

    </div>

  );

}



function CandidateInlineCard({ candidate: c, onAction }) {

  const [summaryExpanded, setSummaryExpanded] = useState(false);
  if (!c) return null;

  const cvcMatchColor = Number(c.match_percentage || c.matchScore) >= 80 ? '#22C55E' : Number(c.match_percentage || c.matchScore) >= 60 ? '#F59E0B' : '#EF4444';
  const sourceTypeMap = { google: 'Online Sourcing', google_search: 'Online Sourcing', linkedin: 'LinkedIn', linkedin_paid: 'LinkedIn', naukri: 'Naukri', resume_box: RESUME_LIBRARY_LABEL, job_post: 'Job Post', job_post_linkedin: 'Job Post - LinkedIn', job_post_naukri: 'Job Post - Naukri', search: 'Search' };
  const sourceTypeLabel = sourceTypeMap[(c.source_type || '').toLowerCase()] || c.source_type || '';
  const candidateSummaryText = String(c.summary || c.summary_text || '').trim();
  const shouldCollapseSummary = candidateSummaryText.length > 220 || candidateSummaryText.includes('\n');
  let computedExpYears = c.experience_years || c.total_experience || null;
  if (!computedExpYears && c.experience) {
    let expArr = c.experience;
    if (typeof expArr === 'string') { try { expArr = JSON.parse(expArr); } catch { expArr = []; } }
    if (Array.isArray(expArr)) {
      let totalMonths = 0;
      expArr.forEach(exp => {
        if (exp.duration) {
          const yMatch = exp.duration.match(/(\d+)\s*y/i);
          const mMatch = exp.duration.match(/(\d+)\s*m/i);
          if (yMatch) totalMonths += parseInt(yMatch[1]) * 12;
          if (mMatch) totalMonths += parseInt(mMatch[1]);
        }
      });
      if (totalMonths > 0) computedExpYears = Math.round(totalMonths / 12 * 10) / 10;
    }
  }

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.border}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: 'var(--dashboard-card, #fff)', width: '100%', maxWidth: 420 }}>
      {/* Card Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: 'var(--dashboard-hover, linear-gradient(135deg, #3B5BDB10, #28ACA808))', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 600, flexShrink: 0 }}>
            {(c.name || c.candidate_name || 'C').charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>{c.name || c.candidate_name || 'Candidate'}</h4>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: C.blueLt, color: C.blue }}>
                {c.funnel_stage || c.status || 'Sourced'}
              </span>
              {sourceTypeLabel && (
                <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 12, background: C.blueLt, color: C.blue }}>{sourceTypeLabel}</span>
              )}
              {computedExpYears && (
                <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 12, background: C.greenLt, color: C.green }}>{computedExpYears}y exp</span>
              )}
            </div>
            {c.title && <p style={{ fontSize: 12, color: C.muted, margin: '2px 0 0' }}>{c.title}{c.company ? ' at ' + c.company : ''}</p>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {c.match_percentage && Number(c.match_percentage) > 0 && (
            <span
              title="Resume/JD match score"
              style={{ fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 6, color: cvcMatchColor, backgroundColor: cvcMatchColor + '10' }}
            >
              {c.match_percentage}% resume match
            </span>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div style={{ padding: '16px' }}>
        {/* Quick Action Buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {c.email && (
            <a href={`mailto:${c.email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: C.blueLt, color: C.blue, fontSize: 12, fontWeight: 500, textDecoration: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              Email
            </a>
          )}
          {c.phone && (
            <a href={`tel:${c.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: C.greenLt, color: C.green, fontSize: 12, fontWeight: 500, textDecoration: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
              Call
            </a>
          )}
          {c.linkedin_profile_url && (
            <a href={c.linkedin_profile_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: C.blueLt, color: C.blue, fontSize: 12, fontWeight: 500, textDecoration: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="14" height="14" fill="#0A66C2" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
          )}
        </div>

        {/* Contact info */}
        <div style={{ padding: 12, borderRadius: 8, background: C.surface, marginBottom: 16, fontSize: 12, color: C.muted }}>
          {c.email && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span>{c.email}</span></div>}
          {c.phone && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg><span>{c.phone}</span></div>}
          {c.location && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg><span>{c.location}</span></div>}
          {c.company && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg><span>{c.company}</span></div>}
          {computedExpYears && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg><span>{computedExpYears} years experience</span></div>}
          {c.current_salary && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg><span>Current Salary: {c.current_salary}</span></div>}
          {c.expected_salary && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/></svg><span>Expected Salary: {c.expected_salary}</span></div>}
          {c.notice_period && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg><span>Notice Period: {c.notice_period}</span></div>}
          {c.portfolio_url && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg><a href={c.portfolio_url} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, textDecoration: 'none' }}>Portfolio</a></div>}
          {c.github_url && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg><a href={c.github_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3B5BDB', textDecoration: 'none' }}>GitHub</a></div>}
        </div>

        {/* Highlights */}
        {c.highlights && (() => {
          let highlightsList = c.highlights;
          if (typeof highlightsList === 'string') { try { highlightsList = JSON.parse(highlightsList); } catch { highlightsList = []; } }
          if (!Array.isArray(highlightsList)) highlightsList = [highlightsList];
          highlightsList = highlightsList.filter(Boolean);
          if (highlightsList.length === 0) return null;
          return (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>Key Highlights</p>
              <div>
                {highlightsList.slice(0, 5).map((hl, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'start', gap: 8, fontSize: 12, marginBottom: 4, color: C.muted }}>
                    <span style={{ color: '#22C55E', flexShrink: 0 }}>✓</span>
                    <span>{typeof hl === 'string' ? hl : hl.text || String(hl)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Skills */}
        {(c.skills || []).length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>Skills</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(c.skills || []).slice(0, 10).map((sk, i) => (
                <span key={i} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 16, background: C.surface, color: C.text, border: `1px solid ${C.border}` }}>{typeof sk === 'string' ? sk : sk.name || String(sk)}</span>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        {candidateSummaryText && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', marginBottom: 8 }}>About</p>
            <p
              title={candidateSummaryText}
              style={{
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.6,
                ...(shouldCollapseSummary && !summaryExpanded
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }
                  : null),
              }}
            >
              {candidateSummaryText}
            </p>
            {shouldCollapseSummary && (
              <button
                type="button"
                onClick={() => setSummaryExpanded((prev) => !prev)}
                style={{
                  marginTop: 6,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: '#1D4ED8',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {summaryExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        )}

        {/* Education - Accordion */}
        {c.education && (() => {
          let eduList = c.education;
          if (typeof eduList === 'string') { try { eduList = JSON.parse(eduList); } catch { eduList = []; } }
          if (!Array.isArray(eduList)) eduList = [eduList];
          eduList = eduList.filter(Boolean);
          if (eduList.length === 0) return null;
          return (
            <details style={{ marginBottom: 16, borderRadius: 8, background: C.surface }}>
              <summary style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', color: C.text, borderRadius: 8, listStyle: 'none' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"/></svg>
                  Education <span style={{ fontSize: 10, fontWeight: 400, color: C.muted }}>({eduList.length})</span>
                </span>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
              </summary>
              <div style={{ padding: '0 12px 12px' }}>
                {eduList.map((edu, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.text, marginBottom: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 2 }}>{edu.degree || edu.name || ''}{edu.field ? ` in ${edu.field}` : ''}</p>
                    {(edu.school || edu.name) && <p style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{edu.school || edu.name}</p>}
                    {(edu.duration || edu.date) && <p style={{ fontSize: 11, color: '#9CA3AF' }}>{edu.duration || edu.date}</p>}
                  </div>
                ))}
              </div>
            </details>
          );
        })()}

        {/* Experience - Accordion */}
        {c.experience && (() => {
          let expList = c.experience;
          if (typeof expList === 'string') { try { expList = JSON.parse(expList); } catch { expList = []; } }
          if (!Array.isArray(expList)) expList = [expList];
          expList = expList.filter(Boolean);
          if (expList.length === 0) return null;
          return (
            <details style={{ borderRadius: 8, background: C.surface }}>
              <summary style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', color: C.text, borderRadius: 8, listStyle: 'none' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  Experience <span style={{ fontSize: 10, fontWeight: 400, color: C.muted }}>({expList.length})</span>
                </span>
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/></svg>
              </summary>
              <div style={{ padding: '0 12px 12px' }}>
                {expList.map((exp, i) => (
                  <div key={i} style={{ fontSize: 12, color: C.text, marginBottom: 12 }}>
                    <p style={{ fontWeight: 600, marginBottom: 2 }}>{exp.title || exp.role || exp.position || ''}</p>
                    {(exp.company || exp.organization) && <p style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{exp.company || exp.organization}</p>}
                    {(exp.duration || exp.date) && <p style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 4 }}>{exp.duration || exp.date}</p>}
                    {exp.description && <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{exp.description}</p>}
                  </div>
                ))}
              </div>
            </details>
          );
        })()}
      </div>
    </div>
  );

}

function RescheduleInlineCard({ reschedule: r }) {

  if (!r) return null;

  const candidateName = r.candidate_name || r.candidate_email || "Candidate";

  const jobTitle = r.job_title || "Interview";

  const status = r.status || "pending";

  const statusColor = status.toLowerCase() === 'approved' ? C.green : status.toLowerCase() === 'pending' ? C.amber : C.red;

  const fields = [

    ["Candidate", candidateName],

    ["Job Title", jobTitle],

    ["Requested Date", r.requested_date || "N/A"],

  ].filter(([,v]) => v !== undefined && v !== null && v !== "" && v !== "N/A");



  return (

    <div style={{ borderRadius:16, border:"1px solid "+C.border, background:"var(--dashboard-card, #fff)", overflow:"hidden", width:"100%", maxWidth:420, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>

      {/* Header with gradient background */}

      <div style={{ background:"linear-gradient(135deg, #FAECE7 0%, #F5D5C8 100%)", padding:"16px 18px", display:"flex", alignItems:"center", gap:14 }}>

        <div style={{ width:44, height:44, borderRadius:"12px", background:"rgba(153, 60, 29, 0.15)", color:"#993C1D", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>

          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6M20 10A8 8 0 005.6 6.2M4 14a8 8 0 0014.4 3.8"/></svg>

        </div>

        <div style={{ flex:1, minWidth:0 }}>

          <p style={{ fontSize:15, fontWeight:600, color:"#993C1D", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{candidateName}</p>

          <p style={{ fontSize:12, color:"rgba(153, 60, 29, 0.7)", margin:"4px 0 0" }}>Reschedule Request</p>

        </div>

        <div style={{ 

          padding:"6px 14px", borderRadius:"20px", fontSize:11, fontWeight:600, textTransform:"capitalize",

          background: status.toLowerCase() === 'approved' ? C.greenLt : status.toLowerCase() === 'pending' ? C.amberLt : C.redLt,

          color: statusColor, border: `1px solid ${status.toLowerCase() === 'approved' ? '#b0ecd4' : status.toLowerCase() === 'pending' ? '#fce0a0' : '#fca5a5'}`

        }}>{status}</div>

      </div>



      {/* Content section */}

      <div style={{ padding:"16px 18px" }}>

        {fields.length > 0 && (

          <div style={{ display:"flex", flexDirection:"column", gap:"12px" }}>

            {fields.map(([l,v]) => (

              <div key={l} style={{ display:"flex", flexDirection:"column", gap:"4px" }}>

                <p style={{ fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:".8px", color:C.muted, margin:0 }}>{l}</p>

                <p style={{ fontSize:13, fontWeight:500, color:C.text, margin:0, lineHeight:1.5 }}>{v}</p>

              </div>

            ))}

          </div>

        )}

      </div>



      {/* Status highlight bar */}

      <div style={{ 

        padding:"12px 18px", borderTop:"1px solid "+C.border, background: status.toLowerCase() === 'approved' ? `${C.green}08` : status.toLowerCase() === 'pending' ? `${C.amber}08` : `${C.red}08`,

        display:"flex", alignItems:"center", gap:8

      }}>

        <div style={{ 

          width:8, height:8, borderRadius:"50%", background: statusColor, flexShrink:0

        }} />

        <p style={{ fontSize:12, fontWeight:500, color: statusColor, margin:0 }}>Status: <span style={{ fontWeight:600 }}>{status}</span></p>

      </div>



      {r.candidate_reason && (

        <div style={{ padding:"14px 18px", borderTop:"1px dashed "+C.border, background:C.surface }}>

          <p style={{ fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:".8px", color:C.muted, margin:"0 0 6px" }}>Candidate Reason</p>

          <p style={{ fontSize:12, color:C.text, margin:0, fontStyle:"italic", lineHeight:1.6 }}>{r.candidate_reason}</p>

        </div>

      )}


    </div>

  );

}



function ResumeInlineCard({ resume: r }) {

  if (!r) return null;

  const fileName = r.file_name || r.name || "Resume";

  const status = r.status || r.parsing_status || "queued";

  const statusColor = status.toLowerCase() === 'completed' || status.toLowerCase() === 'parsed' ? C.green : status.toLowerCase() === 'processing' ? C.blue : status.toLowerCase() === 'failed' ? C.red : C.muted;



  const fields = [

    ["Name", r.name],

    ["Email", r.email],

    ["Phone", r.phone],

    ["Location", r.location],

    ["Experience", r.total_experience_in_months ? `${r.total_experience_in_months} months` : r.total_experience],

    ["Experience Level", r.experience_level],

    ["Education", r.education],

    ["Industry", r.industry_backgrounds],

    ["Company", r.companies_worked_at],

    ["Potential Roles", r.potential_roles],

  ].filter(([,v]) => v !== undefined && v !== null && v !== "" && v !== "N/A");



  return (

    <div style={{ borderRadius:16, border:"1px solid "+C.border, background:"var(--dashboard-card, #fff)", overflow:"hidden", width:"100%", maxWidth:420, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>

      {/* Header with gradient background */}

      <div style={{ background:"linear-gradient(135deg, #E6F1FB 0%, #D4E4FC 100%)", padding:"16px 18px", display:"flex", alignItems:"center", gap:14 }}>

        <div style={{ width:44, height:44, borderRadius:"12px", background:"rgba(24, 95, 165, 0.12)", color:"#185FA5", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>

          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>

        </div>

        <div style={{ flex:1, minWidth:0 }}>

          <p style={{ fontSize:15, fontWeight:600, color:"#185FA5", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.name || fileName}</p>

          <p style={{ fontSize:12, color:"rgba(24, 95, 165, 0.7)", margin:"4px 0 0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.email || "Resume Details"}</p>

        </div>

        <div style={{ 

          padding:"6px 14px", borderRadius:"20px", fontSize:11, fontWeight:600, textTransform:"capitalize",

          background: status.toLowerCase() === 'completed' ? C.greenLt : status.toLowerCase() === 'processing' ? C.blueLt : status.toLowerCase() === 'failed' ? C.redLt : "#f5f5f5",

          color: statusColor, border: `1px solid ${status.toLowerCase() === 'completed' ? '#b0ecd4' : status.toLowerCase() === 'processing' ? C.blueMid : status.toLowerCase() === 'failed' ? '#fca5a5' : '#e0e0e0'}`

        }}>{getResumeStatusLabel(status)}</div>

      </div>



      {/* Content section */}

      <div style={{ padding:"16px 18px" }}>

        {fields.length > 0 && (

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:"12px 16px" }}>

            {fields.map(([l,v]) => (

              <div key={l} style={{ display:"flex", flexDirection:"column", gap:"4px" }}>

                <p style={{ fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:".8px", color:C.muted, margin:0 }}>{l}</p>

                <p style={{ fontSize:12, fontWeight:500, color:C.text, margin:0, lineHeight:1.5, wordBreak:"break-word" }}>{v}</p>

              </div>

            ))}

          </div>

        )}

      </div>



      {/* Footer with PDF button */}

      <div style={{ padding:"14px 18px", borderTop:"1px solid "+C.border, background:C.surface }}>

        {r.pdf_url || r.file_url || r.s3_url ? (

          <a

            href={r.pdf_url || r.file_url || r.s3_url}

            target="_blank"

            rel="noreferrer"

            style={{

              display:"inline-flex",

              alignItems:"center",

              justifyContent:"center",

              gap:8,

              width:"100%",

              padding:"10px 16px",

              borderRadius:10,

              fontSize:12,

              fontWeight:600,

              background:C.blue,

              color:"#fff",

              border:"none",

              textDecoration:"none",

              cursor:"pointer",

              transition:"background 0.2s"

            }}

            onMouseEnter={(e) => e.target.style.background = "#2a4bc4"}

            onMouseLeave={(e) => e.target.style.background = C.blue}

          >

            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>

            View Resume PDF

          </a>

        ) : (

          <div style={{ 

            padding:"10px 16px", 

            borderRadius:10, 

            fontSize:12, 

            fontWeight:500, 

            textAlign:"center", 

            background:C.surface,

            color:C.muted,

            border:"1px dashed "+C.border

          }}>

            PDF not available

          </div>

        )}

      </div>

    </div>

  );

}

function InterviewInlineCard({ interview: iv }) {
  if (!iv) return null;
  const candidateName = iv.candidate_name || iv.candidate_email || "Candidate";
  const jobTitle = iv.job_title || iv.interview_title || "Interview";
  const status = iv.status || iv.ai_note_taker_status || "scheduled";
  const fields = [
    ["Candidate", candidateName],
    ["Job Title", jobTitle],
    ["Date", iv.scheduled_date ? new Date(iv.scheduled_date).toLocaleDateString() : "N/A"],
    ["Time", iv.scheduled_time || "N/A"],
    ["Interviewer", iv.interviewer_name || "N/A"],
    ["Status", status],
  ].filter(([,v]) => v !== undefined && v !== null && v !== "" && v !== "N/A");

  return (
    <div style={{ borderRadius:12, border:"0.5px solid "+C.border, background:"transparent", overflow:"hidden", width:"100%", maxWidth:420 }}>
      <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:12, borderBottom:`0.5px solid ${C.border}` }}>
        <div style={{ width:38, height:38, borderRadius:"50%", background:"#FAEEDA", color:"#854F0B", fontSize:13, fontWeight:500, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{candidateName}</p>
          <p style={{ fontSize:12, color:C.muted, margin:"3px 0 0" }}>{jobTitle}</p>
        </div>
        <Badge status={status} />
      </div>

      {fields.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px 16px", padding:"12px 14px" }}>

          {fields.map(([l,v]) => (

            <div key={l}>

              <p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", color:C.muted, margin:"0 0 2px" }}>{l}</p>

              <p style={{ fontSize:11, fontWeight:500, color:C.text, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{v}</p>

            </div>

          ))}

        </div>

      )}

      {iv.google_meet_link && (

        <div style={{ padding:"10px 14px", borderTop:`0.5px solid ${C.border}`, background:C.surface }}>

          <a href={iv.google_meet_link} target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:5, background:C.blue, color:"#fff", borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:500, textDecoration:"none" }}>

            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg>

            Join Google Meet

          </a>

        </div>

      )}


    </div>

  );

}

function ThinkingDots() {

  return (

    <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>

      <div style={{ width:28, height:28, borderRadius:"50%", background:C.blue, color:"#fff", display:"flex", alignItems:"center", justifyContent:"center" }}>

        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>

      </div>

      <div style={{ display:"flex", alignItems:"center", gap:5, borderRadius:"3px 12px 12px 12px", padding:"12px 16px", background:C.surface, border:`1px solid ${C.border}` }}>

        {[0,160,320].map(d => (

          <span key={d} style={{ width:6, height:6, borderRadius:"50%", background:C.blue, display:"inline-block", animation:`bounce 1s ${d}ms infinite` }} />

        ))}

      </div>

      <style>{`@keyframes bounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}`}</style>

    </div>

  );

}



/* ─────────────────────────────────────────────────────────────

   PANELS

───────────────────────────────────────────────────────────── */

/* Safe string coercion — never renders a plain object */
const safeStr = v => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) return v.map(safeStr).filter(Boolean).join(", ");
  if (typeof v === "object") return v.title || v.name || v.label || JSON.stringify(v);
  return String(v);
};
/* — Master Database — */

function MasterDatabasePanel({ onSelectCandidate }) {
  const navigate = useNavigate();

  const [q, setQ]     = useState("");

  const [tab, setTab] = useState("All");

  const [sel, setSel] = useState(null);

  const [rows, setRows] = useState(null);

  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const tabs = ["All","Interested","Shortlisted"];



  const load = useCallback(async () => {

    setLoading(true);

    try {

      const limit = 200;

      const allRows = [];

      

      // Always fetch all data without search parameter

      const countRes = await aiAutomationApi.get("/automation_candidates/all?limit=1");

      console.log("Count API response:", countRes);

      const total = countRes?.total_records || countRes?.total || countRes?.count || countRes?.total_count || countRes?.length || 0;

      console.log("Total count extracted:", total);

      setTotalCount(total);



      const totalPages = Math.ceil(total / limit);

      console.log("Total pages:", totalPages);



      for (let page = 0; page < totalPages; page++) {

        const offset = page * limit;

        const params = new URLSearchParams();

        params.set("limit", String(limit));

        params.set("offset", String(offset));

        const res = await aiAutomationApi.get(`/automation_candidates/all?${params.toString()}`);

        console.log(`Page ${page} response:`, res);

        const batch = Array.isArray(res?.data||res?.candidates||res) ? (res?.data||res?.candidates||res) : [];

        console.log(`Page ${page} batch size:`, batch.length);

        allRows.push(...batch);

      }



      console.log("Total rows loaded:", allRows.length);

      setRows(allRows);

    } catch (err) {

      console.error("Error loading master database:", err);

      setRows([]);

      setTotalCount(0);

    }

    finally { setLoading(false); }

  }, []);



  useEffect(() => { load(); }, [load]);



  const filtered = (rows||[]).filter(c => {

    const matchTab = tab==="All"?true:tab==="Active"?(c.status||c.funnel_stage||"").toLowerCase()==="active":tab==="Shortlisted"?(c.status||c.funnel_stage||"").toLowerCase()==="shortlisted":((c.status||c.funnel_stage||"").toLowerCase()==="hired");

    const matchSearch = !q || [c.name,c.candidate_name,c.email,c.current_title,c.job_title,c.location].some(v=>(v||"").toLowerCase().includes(q.toLowerCase()));

    return matchTab && matchSearch;

  });



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%", background:"var(--dashboard-surface, #fafbff)" }}>

      <div style={{ padding:"12px 14px 6px" }}>
        <div style={{ background:"var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%))", border:`1px solid ${C.border}`, borderRadius:16, padding:"14px 16px", boxShadow:"0 12px 24px rgba(15, 23, 42, 0.04)", display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:14, fontWeight:700, color:C.text }}>Master Database</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>Browse candidates in the dashboard, or open the full workspace for detailed management.</div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", justifyContent:"flex-end" }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"7px 12px", borderRadius:999, background:C.surface, border:`1px solid ${C.border}`, color:C.blue, fontSize:11, fontWeight:700 }}>
              <span>{filtered.length}</span>
              <span style={{ color:C.muted, fontWeight:600 }}>matching candidates</span>
            </div>
            <button
              type="button"
              onClick={() => navigate('/master-database?from_dashboard=true')}
              style={{
                borderRadius:999,
                padding:"8px 14px",
                fontSize:11,
                fontWeight:700,
                cursor:"pointer",
                border:`1px solid ${C.blueMid}`,
                background:"var(--dashboard-card, #FFFFFF)",
                color:C.blue,
                boxShadow:"0 8px 18px rgba(15, 23, 42, 0.04)",
              }}
            >
              Open Full View
            </button>
          </div>
        </div>
      </div>

      {/* Search bar with improved styling */}

      <div style={{ padding:"10px 14px" }}>

        <input 

          value={q} 

          onChange={e => setQ(e.target.value)} 

          placeholder="Search by name, role, location..." 

          style={{

            width:"100%", background:"transparent", border:"none", borderRadius:0,

            padding:"7px 0", fontSize:12, outline:"none", color:C.text, boxSizing:"border-box",

          }}

        />

      </div>



      {/* Candidate list with improved styling */}

      <div style={{ flex:1, overflowY:"auto", padding:"8px 12px" }}>

        {rows===null ? <Loader /> : filtered.length===0 ? <Empty text="No candidates found" /> :

          filtered.map((c,i) => {

            const uniqueKey = `${c.id || c.email || c.name || 'candidate'}_${i}_${Date.now()}`;

            const avatarColors = [
              { bg:"var(--dashboard-blue-soft, #E6F1FB)", color:"var(--dashboard-blue, #185FA5)" },
              { bg:"var(--dashboard-green-soft, #E1F5EE)", color:"var(--dashboard-green, #0F6E56)" },
              { bg:"var(--dashboard-amber-soft, #FAEEDA)", color:"var(--dashboard-amber, #854F0B)" },
              { bg:"var(--dashboard-blue-soft, #EEEDFE)", color:"var(--dashboard-blue, #534AB7)" },
              { bg:"var(--dashboard-red-soft, #FAECE7)", color:"var(--dashboard-red, #993C1D)" }
            ];

            const av = avatarColors[i % avatarColors.length];

            const statusBadgeColors = {
              identified: { bg:C.surface, color:C.muted },
              interview: { bg:C.greenLt, color:C.green },
              interested: { bg:C.amberLt, color:C.amber },
              offer: { bg:C.blueLt, color:C.blue },
              active: { bg:C.surface, color:C.muted }
            };

            const status = (c.funnel_stage||c.status||"active").toLowerCase();
            const badge = statusBadgeColors[status] || statusBadgeColors.active;

            return (

              <React.Fragment key={uniqueKey}>

                {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}

                <div

                  onClick={() => { setSel(c.id); onSelectCandidate?.(c); }}

                  style={{

                    display:"flex",

                    alignItems:"center",

                    gap:12,

                    padding:"12px 14px",

                    borderRadius:12,

                    marginBottom:8,

                    border:sel===c.id?"1px solid "+C.blue:`1px solid ${C.border}`,

                    cursor:"pointer",

                    background:sel===c.id?"var(--dashboard-hover, #F7F8FF)":"var(--dashboard-card, #fff)",

                    transition:"all 0.2s ease",

                  }}

                  onMouseEnter={e => { if (sel!==c.id) { e.currentTarget.style.background = "var(--dashboard-hover, #F5F3FF)"; e.currentTarget.style.borderColor = "var(--dashboard-blue-border, #C7D2FE)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; } }}

                  onMouseLeave={e => { if (sel!==c.id) { e.currentTarget.style.background = "var(--dashboard-card, #fff)"; e.currentTarget.style.borderColor = "var(--dashboard-border, #ECEEF8)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}

                >

                  <div style={{ width:40, height:40, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:14, fontWeight:500, flexShrink:0 }}>

                    {(c.name||c.candidate_name||"C")[0].toUpperCase()}

                  </div>
<div style={{ flex:1, minWidth:0 }}>
  <div style={{ fontSize:13, fontWeight:500, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginBottom:5 }}>
    {safeStr(c.name||c.candidate_name||"Candidate")}
  </div>
  {(() => {
    const locationVal = typeof c.location === "string" ? c.location.trim() : "";
    const sourceRaw = c.linkedin_profile_url || c.linkedin_url
      ? "LinkedIn"
      : typeof c.source === "string" && c.source.trim()
        ? c.source.trim()
        : typeof c.platform === "string" && c.platform.trim()
          ? c.platform.trim()
          : "";
    const isNaukri = sourceRaw.toLowerCase().includes("naukri");

    const expMonths = c.total_experience_in_months;
    const expRaw = c.total_experience;
    const expVal = expMonths && typeof expMonths === "number"
      ? `${Math.floor(expMonths / 12)}y exp`
      : typeof expRaw === "string" && expRaw.trim()
        ? expRaw.trim()
        : typeof expRaw === "number"
          ? `${expRaw}y exp`
          : "";

    return (
      <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4, flexWrap:"wrap" }}>
        {locationVal ? (
          <MetaPill type="loc" icon={<PinIcon color="#A32D2D" />}>
            {locationVal}
          </MetaPill>
        ) : null}
        {sourceRaw ? (
          <MetaPill type={isNaukri ? "naukri" : "src"}>
            {sourceRaw}
          </MetaPill>
        ) : null}
        {expVal ? (
          <MetaPill type="exp">{expVal}</MetaPill>
        ) : null}
      </div>
    );
  })()}
</div>

                  <div style={{ padding:"3px 10px", borderRadius:"100px", fontSize:11, fontWeight:500, letterSpacing:"0.02em", flexShrink:0, background:badge.bg, color:badge.color }}>

                    {(c.funnel_stage||c.status||"active").charAt(0).toUpperCase() + (c.funnel_stage||c.status||"active").slice(1)}

                  </div>

                </div>

              </React.Fragment>

            );

          })}

      </div>

    </div>

  );

}



/* — My Interviews — */

function MyInterviewsPanel({ onShowInterview }) {

  const [rows, setRows]   = useState(null);

  const [q, setQ]         = useState("");

  const [tab, setTab]     = useState("upcoming");

  const [selInterview, setSelInterview] = useState(null);



  useEffect(() => {

    const m = getMetadata(); const cid = m?.cid;
    aiAutomationApi.get(`/interviews/calendar/all${cid ? `?cid=${cid}` : ""}`).then(d => setRows(d?.interviews||[])).catch(()=>setRows([]));

  }, []);



  const now = new Date();

  const filtered = useMemo(() => {

    if (!rows) return [];

    let list = rows;

    if (tab==="upcoming") list = rows.filter(iv => new Date(`${iv.scheduled_date||""}T${iv.scheduled_time||"00:00:00"}`) >= now);

    else list = rows.filter(iv => new Date(`${iv.scheduled_date||""}T${iv.scheduled_time||"00:00:00"}`) < now);

    if (q.trim()) { const lo=q.toLowerCase(); list=list.filter(iv=>[iv.candidate_name,iv.candidate_email,iv.job_title].some(v=>(v||"").toLowerCase().includes(lo))); }

    return list;

  }, [rows, tab, q]);



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <div style={{ padding:"6px 14px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>

        <span style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", color:C.muted }}>{filtered.length} {tab}</span>

        <div style={{ display:"flex", gap:4 }}>

          {["upcoming","past"].map(t => (

            <button key={t} onClick={() => setTab(t)} style={{ borderRadius:7, padding:"3px 10px", fontSize:10, fontWeight:600, cursor:"pointer", textTransform:"capitalize", background:tab===t?C.blue:"transparent", color:tab===t?"#fff":C.muted, border:"none" }}>{t}</button>

          ))}

        </div>

      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"8px 12px" }}>

        {rows===null ? <Loader /> : filtered.length===0 ? <Empty text="No interviews found" /> :

          filtered.slice(0,20).map((iv,i) => {
            const avatarColors = [
              { bg:"#E6F1FB", color:"#185FA5" },
              { bg:"#E1F5EE", color:"#0F6E56" },
              { bg:"#FAEEDA", color:"#854F0B" },
              { bg:"#EEEDFE", color:"#534AB7" },
              { bg:"#FAECE7", color:"#993C1D" }
            ];

            const av = avatarColors[i % avatarColors.length];

            const statusBadgeColors = {
              scheduled: { bg:"#E1F5EE", color:"#0F6E56" },
              completed: { bg:"#F1EFE8", color:"#5F5E5A" },
              cancelled: { bg:"#FAECE7", color:"#993C1D" }
            };

            const status = (iv.status||"scheduled").toLowerCase();
            const badge = statusBadgeColors[status] || statusBadgeColors.scheduled;

            return (
              <React.Fragment key={iv.id||i}>
                {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
                <div 
                  onClick={() => onShowInterview?.(iv)}
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:12,
                    padding:"12px 14px",
                    borderRadius:12,
                    marginBottom:8,
                    border:selInterview===iv.id?"1px solid "+C.blue:"1px solid #ECEEF8",
                    cursor:"pointer",
                    background:selInterview===iv.id?"#F7F8FF":"#fff",
                    transition:"all 0.2s ease",
                  }}
                  onMouseEnter={e => { if (selInterview!==iv.id) { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.borderColor = "#C7D2FE"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; } }}
                  onMouseLeave={e => { if (selInterview!==iv.id) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#ECEEF8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
                >
                  <div style={{ width:40, height:40, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:14, fontWeight:500, flexShrink:0 }}>
                    {(iv.candidate_name||iv.candidate_email||"C")[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                      {iv.candidate_name||iv.candidate_email||"Candidate"}
                    </div>
<div style={{ display:"flex", alignItems:"center", gap:4, marginTop:5, flexWrap:"wrap" }}>
  {iv.location && (
    <MetaPill type="loc" icon={<PinIcon color="#5F5E5A" />}>
      {iv.location}
    </MetaPill>
  )}
  <MetaPill type="time">
    {fmtDate(`${iv.scheduled_date||""} ${iv.scheduled_time||""}`)}
  </MetaPill>
</div>
                  </div>
                  <div style={{ padding:"3px 10px", borderRadius:"100px", fontSize:11, fontWeight:500, letterSpacing:"0.02em", flexShrink:0, background:badge.bg, color:badge.color }}>
                    {iv.status||"scheduled"}
                  </div>
                </div>
{tab==="upcoming" && iv.google_meet_link && (
  <div style={{ padding:"2px 10px 8px 60px" }}>
    <a
      href={iv.google_meet_link}
      target="_blank"
      rel="noreferrer"
      onClick={e => e.stopPropagation()}
      style={{
        display:"inline-flex", alignItems:"center", gap:5,
        background:"#E6F1FB", color:"#185FA5",
        border:"0.5px solid #B5D4F4",
        borderRadius:8, padding:"5px 12px",
        fontSize:11, fontWeight:500, textDecoration:"none",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#185FA5" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
      </svg>
      Join Meet
    </a>
  </div>
)}
                {(iv.notes || iv.meeting_notes) && (
                  <div style={{ padding:"0 12px 12px 12px" }}>
                    <div style={{ padding:"8px 12px", background:C.surface, borderRadius:8, fontSize:11, color:C.muted, lineHeight:1.5 }}>
                      {iv.notes || iv.meeting_notes}
                    </div>
                  </div>
                )}
                

              </React.Fragment>
            );
          })

        }

      </div>

    </div>

  );

}



/* — Reschedule — */

function ReschedulePanel({ onShowReschedule }) {

  const [rows, setRows]     = useState(null);

  const [statusTab, setStatusTab] = useState("active");

  const [q, setQ]           = useState("");

  const [msg, setMsg]       = useState("");

  const [count, setCount]   = useState(0);

  const [totalCount, setTotalCount] = useState(0);



  const load = useCallback(async () => {

    try {

      const params = new URLSearchParams();

      if (statusTab==="active") { params.append("status","pending"); params.append("status","needs_manual_action"); }

      else if (statusTab!=="all") params.set("status",statusTab);

      const data = await aiAutomationApi.get(`/interviews/reschedule-requests?${params.toString()}`);

      const list = Array.isArray(data) ? data : [];

      setRows(list);

      setCount(list.filter(r=>r.status==="pending"||r.status==="needs_manual_action").length);

      setTotalCount(list.length);

    } catch { setRows([]); setCount(0); setTotalCount(0); }

  }, [statusTab]);



  useEffect(() => { load(); }, [load]);



  const act = async (id, type) => {

    try {

      if (type==="approve") {

        const r = (rows||[]).find(x=>x.id===id);

        const result = await aiAutomationApi.post(`/interviews/reschedule-requests/${id}/approve`, { scheduled_date: r?.requested_date||new Date().toISOString().split("T")[0], scheduled_time:"10:00" });

        if (result?.gmail_auth_needed || result?.mail_auth_needed || isMailProviderAuthError(result)) {
          const interviewId = result?.interview_id || result?.interview?.id || r?.interview_id;
          setMsg("Approved. Mail reconnect is required to send the confirmation email.");
          await handleMailProviderReconnect({
            cid: getCid(),
            error: result,
            onConnected: async () => {
              if (!interviewId) {
                setMsg("Mail connected, but confirmation email could not be retried automatically.");
                return;
              }
              try {
                const resend = await aiAutomationApi.post(`/interviews/${interviewId}/resend-confirmation`, {});
                if (resend?.success) {
                  setMsg("✓ Approved. Confirmation email sent.");
                } else if (resend?.gmail_auth_needed || resend?.mail_auth_needed || isMailProviderAuthError(resend)) {
                  setMsg("Approved, but mail provider is still not connected.");
                } else {
                  setMsg("Approved, but confirmation email could not be sent.");
                }
              } catch (err) {
                setMsg(err?.message || "Approved, but confirmation email could not be sent.");
              }
            },
            onError: (msg) => setMsg(msg || "Mail reconnect was cancelled or failed."),
          });
        } else {
          setMsg("✓ Approved.");
        }

      } else {

        await aiAutomationApi.post(`/interviews/reschedule-requests/${id}/reject`, { decline_reason:"Rejected from dashboard" });

      }

      setRows(p => (p||[]).filter(x => x.id!==id)); setCount(c => Math.max(0,c-1));

      if (type !== "approve") setMsg("Rejected.");

    } catch (err) { setMsg(err?.message||"Action failed."); }

  };



  const filtered = (rows||[]).filter(r => {

    const matchSearch = !q || [r.candidate_name,r.candidate_email,r.job_title].some(v=>(v||"").toLowerCase().includes(q.toLowerCase()));

    const matchStatus = statusTab==="all"?true:statusTab==="active"?(r.status==="pending"||r.status==="needs_manual_action"):r.status===statusTab;

    return matchSearch && matchStatus;

  });



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <PanelSearch value={q} onChange={setQ} placeholder="Search reschedule requests..." />

      <div style={{ padding:"8px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:6 }}>

        {[{id:"active",label:"Active"},{id:"approved",label:"Approved"},{id:"rejected",label:"Rejected"},{id:"all",label:"All"}].map(t => (

          <button key={t.id} onClick={() => setStatusTab(t.id)} style={{ borderRadius:7, padding:"4px 10px", fontSize:10, fontWeight:600, cursor:"pointer", background:statusTab===t.id?C.blue:"transparent", color:statusTab===t.id?"#fff":C.muted, border:statusTab===t.id?"none":`1px solid ${C.border}` }}>{t.label}</button>

        ))}

        <span style={{ marginLeft:"auto", background:"#EF4444", color:"#fff", borderRadius:"50%", width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700 }}>{totalCount}</span>

      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"8px 12px" }}>

        {rows===null ? <Loader /> : filtered.length===0 ? <Empty text="No reschedule requests" /> :

          filtered.map((r,i) => {
            const avatarColors = [
              { bg:"#E6F1FB", color:"#185FA5" },
              { bg:"#E1F5EE", color:"#0F6E56" },
              { bg:"#FAEEDA", color:"#854F0B" },
              { bg:"#EEEDFE", color:"#534AB7" },
              { bg:"#FAECE7", color:"#993C1D" }
            ];

            const av = avatarColors[i % avatarColors.length];

            const statusBadgeColors = {
              pending: { bg:"#FAECE7", color:"#993C1D" },
              approved: { bg:"#E1F5EE", color:"#0F6E56" },
              rejected: { bg:"#F1EFE8", color:"#5F5E5A" }
            };

            const status = (r.status||"pending").toLowerCase();
            const badge = statusBadgeColors[status] || statusBadgeColors.pending;

            return (
              <React.Fragment key={r.id||i}>
                {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
<div
                onClick={() => onShowReschedule?.(r)}
                style={{
  display:"flex",
  alignItems:"center",
  gap:12,
  padding:"12px 14px",
  borderRadius:12,
  marginBottom:8,
  border: (r.status==="pending"||r.status==="needs_manual_action") ? "1px solid #F7C1C1" : "1px solid #ECEEF8",
  cursor:"pointer",
  background: (r.status==="pending"||r.status==="needs_manual_action") ? "#FCEBEB" : "#fff",
  transition:"all 0.2s ease",
                }}
                onMouseEnter={e => { if (!(r.status==="pending"||r.status==="needs_manual_action")) { e.currentTarget.style.background = "#F5F3FF"; e.currentTarget.style.borderColor = "#C7D2FE"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; } }}
                onMouseLeave={e => { if (!(r.status==="pending"||r.status==="needs_manual_action")) { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#ECEEF8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:14, fontWeight:500, flexShrink:0 }}>
                    {(r.candidate_name||r.candidate_email||"C")[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginBottom:6 }}>
                      {r.candidate_name||r.candidate_email||"Candidate"}
                    </div>
<div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
  {r.location && (
    <MetaPill type="loc" icon={<PinIcon color="#5F5E5A" />}>
      {r.location}
    </MetaPill>
  )}
  {r.requested_date && (
    <MetaPill type="alert">Requested {r.requested_date}</MetaPill>
  )}
</div>
                  </div>
                  <div style={{ padding:"3px 10px", borderRadius:"100px", fontSize:11, fontWeight:500, letterSpacing:"0.02em", flexShrink:0, background:badge.bg, color:badge.color }}>
                    {r.status||"pending"}
                  </div>
                </div>
                {r.candidate_reason && (
                  <div style={{ padding:"0 12px 12px 12px" }}>
                    <div style={{ padding:"8px 12px", background:C.surface, borderRadius:8, fontSize:11, color:C.muted, lineHeight:1.5 }}>
                      {r.candidate_reason}
                    </div>
                  </div>
                )}
                {(r.status==="pending"||r.status==="needs_manual_action") && (
                  <div style={{ padding:"0 12px 12px 12px" }}>
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={() => act(r.id,"approve")} style={{ borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:500, background:"#0F6E56", color:"#fff", border:"none", cursor:"pointer" }}>Approve</button>
                      <button onClick={() => act(r.id,"reject")} style={{ borderRadius:8, padding:"6px 12px", fontSize:11, fontWeight:500, background:C.surface, color:C.text, border:"0.5px solid "+C.border, cursor:"pointer" }}>Reject</button>
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })

        }

      </div>

      {msg && <p style={{ fontSize:11, color:C.muted, padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>{msg}</p>}

    </div>

  );

}



/* — Notifications — */

function NotificationsPanel() {

  const [rows, setRows]   = useState(null);

  const [q, setQ]         = useState("");

  const [statusMsg, setStatusMsg] = useState("");



  useEffect(() => {

    const m = getMetadata(); const cid=m?.cid;

    aiAutomationApi.get(`/interviews/action-notifications${cid?`?cid=${cid}`:""}`)

      .then(d => setRows(d?.notifications||[]))

      .catch(() => setRows([]));

  }, []);



  const markAll = async () => {

    try {

      const m = getMetadata(); const cid=m?.cid;

      await aiAutomationApi.patch(`/interviews/notifications/clear-all${cid?`?cid=${cid}`:""}`);

      setRows(p => (p||[]).map(n=>({...n,view_status:true}))); setStatusMsg("All marked read.");

    } catch { setStatusMsg("Failed."); }

  };



  const filtered = (rows||[]).filter(n => !q || (n.info||n.reason||n.content||"").toLowerCase().includes(q.toLowerCase()));



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <PanelSearch value={q} onChange={setQ} placeholder="Search notifications..." />

      <div style={{ padding:"8px 14px", borderBottom:`1px solid ${C.border}` }}>

        <button onClick={markAll} style={{ borderRadius:8, border:`1px solid ${C.border}`, padding:"5px 12px", fontSize:11, fontWeight:500, color:C.muted, background:"var(--dashboard-card, #fff)", cursor:"pointer" }}>Mark all read</button>

      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"8px 12px" }}>

        {rows===null ? <Loader /> : filtered.length===0 ? <Empty text="No notifications" /> :

          filtered.slice(0,20).map((n,idx) => {
            return (
              <React.Fragment key={n.notification_id||n.id||idx}>
                {idx > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
                <div
                  onClick={() => setRows(p=>(p||[]).map(x=>(x.notification_id||x.id)===( n.notification_id||n.id)?{...x,view_status:true}:x))}
                  style={{
                    display:"flex",
                    alignItems:"center",
                    gap:12,
                    padding:"12px 14px",
                    borderRadius:12,
                    marginBottom:8,
                    border:!n.view_status?"1px solid "+C.blue:`1px solid ${C.border}`,
                    cursor:"pointer",
                    background:!n.view_status?C.blueLt:"var(--dashboard-card, #fff)",
                    transition:"all 0.2s ease",
                  }}
                  onMouseEnter={e => { if (n.view_status) { e.currentTarget.style.background = "var(--dashboard-hover, #F5F3FF)"; e.currentTarget.style.borderColor = C.blueMid; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; } }}
                  onMouseLeave={e => { if (n.view_status) { e.currentTarget.style.background = "var(--dashboard-card, #fff)"; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
                >
                  <div style={{ width:40, height:40, borderRadius:"50%", background:!n.view_status?"#EEEDFE":"#F1EFE8", display:"flex", alignItems:"center", justifyContent:"center", color:!n.view_status?"#534AB7":"#5F5E5A", fontSize:14, fontWeight:500, flexShrink:0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/></svg>
                  </div>
<div style={{ flex:1, minWidth:0 }}>
  <div style={{ fontSize:12, color:C.text, lineHeight:1.55, marginBottom:4 }}>
    {n.info||n.reason||n.content||"Notification"}
  </div>
  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
    {n.location && (
      <MetaPill type="loc" icon={<PinIcon color="#5F5E5A" />}>
        {n.location}
      </MetaPill>
    )}
    <span style={{ fontSize:10, color:C.muted }}>
      {n.created_at ? fmtDate(n.created_at) : (!n.view_status ? "New" : "Read")}
    </span>
  </div>
</div>
                  {!n.view_status && <span style={{ width:8, height:8, borderRadius:"50%", background:C.blue, flexShrink:0 }} />}
                </div>
                <div style={{ padding:"0 12px 12px 12px" }}>
                  <p style={{ fontSize:12, color:C.text, lineHeight:1.55, margin:0 }}>{n.info||n.reason||n.content||"Notification"}</p>
                </div>
              </React.Fragment>
            );
          })

        }

      </div>

      {statusMsg && <p style={{ fontSize:11, color:C.muted, padding:"6px 14px" }}>{statusMsg}</p>}

    </div>

  );

}



/* — Show Candidates (by session) — */

function ShowCandidatesPanel({ onSelectCandidate }) {

  const [sessions, setSessions]             = useState(null);

  const [sessionCandidates, setSessionCandidates] = useState({});

  const [sessionPagination, setSessionPagination] = useState({});

  const [sessionLoading, setSessionLoading]       = useState({});

  const [expanded, setExpanded]             = useState({});

  const [loading, setLoading]               = useState(true);

  const [searchQuery, setSearchQuery]       = useState("");

  const ITEMS_PER_PAGE = 10;


  // Load candidates for a specific session with pagination
  const loadSessionCandidates = async (sessionId, page = 1) => {
    const sid = String(sessionId);
    setSessionLoading(prev => ({ ...prev, [sid]: true }));

    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const data = await aiAutomationApi.get(
        `/automation_candidates/all?session_id=${encodeURIComponent(sid)}&limit=${ITEMS_PER_PAGE}&offset=${offset}`
      );

      const cands = data?.data||data?.candidates||data||[];
      const totalRecords = data?.total_records || data?.total || 0;
      const totalPages = Math.ceil(totalRecords / ITEMS_PER_PAGE);

      if (Array.isArray(cands)) {
        setSessionCandidates(prev => ({ ...prev, [sid]: cands }));
        setSessionPagination(prev => ({ 
          ...prev, 
          [sid]: { 
            currentPage: page, 
            totalPages, 
            totalRecords,
            hasMore: page < totalPages 
          } 
        }));
      }
    } catch {
      setSessionCandidates(prev => ({ ...prev, [sid]: [] }));
      setSessionPagination(prev => ({ ...prev, [sid]: { currentPage: 1, totalPages: 1, totalRecords: 0, hasMore: false } }));
    } finally {
      setSessionLoading(prev => ({ ...prev, [sid]: false }));
    }
  };

  // Handle session expand/collapse
  const handleToggleSession = async (sessionId) => {
    const sid = String(sessionId);
    const isOpen = !expanded[sid];
    
    setExpanded(prev => ({ ...prev, [sid]: isOpen }));
    
    if (isOpen && !sessionCandidates[sid]) {
      await loadSessionCandidates(sid, 1);
    }
  };

  // Handle pagination
  const handlePageChange = async (sessionId, newPage) => {
    const sid = String(sessionId);
    await loadSessionCandidates(sid, newPage);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await aiAutomationApi.get("/sessions?limit=50");
        const rows = Array.isArray(res?.sessions||res?.data||res) ? (res?.sessions||res?.data||res) : [];
        const normalized = rows.map(s => ({ ...s, _label: resolveSessionTitle(s) }));
        setSessions(normalized);
      } catch { setSessions([]); } finally { setLoading(false); }
    })();
  }, []);

  const sessionsWithCandidates = (sessions||[]);



  const filterCandidates = (candidates, query) => {

    if (!query.trim()) return candidates;

    const lowerQuery = query.toLowerCase();

    return candidates.filter(c => {

      const name = (c.name||c.candidate_name||"").toLowerCase();

      const email = (c.candidate_email||c.email||"").toLowerCase();

      const title = (c.current_title||"").toLowerCase();

      return name.includes(lowerQuery) || email.includes(lowerQuery) || title.includes(lowerQuery);

    });

  };



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <PanelSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search candidates..." />

      <div style={{ flex:1, overflowY:"auto" }}>

        {loading ? <Loader /> : sessionsWithCandidates.length===0 ? <Empty text="No sessions with candidates found" /> :

          sessionsWithCandidates.map(s => {

            const sid = String(s.id);

            const cands = sessionCandidates[sid]||[];

            const pagination = sessionPagination[sid] || { currentPage: 1, totalPages: 1, totalRecords: 0 };

            const filteredCands = filterCandidates(cands, searchQuery);

            if (filteredCands.length === 0 && searchQuery.trim()) return null;

            const isOpen = expanded[sid];

            const isLoading = sessionLoading[sid];

            return (

              <div key={sid} style={{ marginBottom:8 }}>

                <button onClick={() => handleToggleSession(sid)} style={{

                  width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",

                  padding:"14px", borderRadius:12, background:isOpen ? "var(--dashboard-hover, #F7F8FF)" : "var(--dashboard-card, #fff)", border:`1px solid ${isOpen ? C.blueMid : C.border}`, cursor:"pointer",
                  transition:"all 0.2s ease",
                  position:isOpen ? "sticky" : "static",
                  top:isOpen ? "0" : "auto",
                  zIndex:isOpen ? 10 : "auto",
                  boxShadow:isOpen ? "0 2px 8px rgba(0,0,0,0.1)" : "none",

                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--dashboard-blue-border, #C7D2FE)"; e.currentTarget.style.background = "var(--dashboard-hover, #F7F8FF)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = isOpen ? "0 4px 12px rgba(79, 70, 229, 0.15)" : "0 4px 12px rgba(79, 70, 229, 0.1)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = isOpen ? "var(--dashboard-blue-border, #C7D2FE)" : "var(--dashboard-border, #ECEEF8)"; e.currentTarget.style.background = isOpen ? "var(--dashboard-hover, #F7F8FF)" : "var(--dashboard-card, #fff)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = isOpen ? "0 2px 8px rgba(0,0,0,0.1)" : "none"; }}>

                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>

                    <div style={{ width:40, height:40, borderRadius:10, background:"#8B5CF6", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:16 }}>{(s._label||"S")[0].toUpperCase()}</div>

                    <div style={{ textAlign:"left" }}>

                      <p style={{ fontSize:14, fontWeight:600, color:C.text, margin:0 }}>{s._label}</p>

                      <p style={{ fontSize:11, color:C.muted, margin:0 }}>{pagination.totalRecords > 0 ? `${pagination.totalRecords} candidates` : 'Loading...'}</p>

                    </div>

                  </div>

                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>

                    {isLoading && isOpen && <svg style={{ animation: "spin 1s linear infinite", width: 12, height: 12 }} viewBox="0 0 24 24" fill="none">

                      <circle cx="12" cy="12" r="10" stroke={C.blue} strokeWidth="3" strokeDasharray="31 63" />

                      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

                    </svg>}

                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" style={{ transform:isOpen?"rotate(180deg)":"none", transition:"transform .2s" }}><path d="M19 9l-7 7-7-7"/></svg>

                  </div>

                </button>

                {isOpen && (

                  <div style={{ padding:"8px 12px" }}>

                    {isLoading ? (
                      Array.from({ length: ITEMS_PER_PAGE }).map((_, i) => (
                        <React.Fragment key={`skeleton-${i}`}>
                          {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
                          <CandidateSkeleton />
                        </React.Fragment>
                      ))
                    ) : filteredCands.length > 0 ? (
                      filteredCands.map((c,i) => {
                        const avatarColors = [
                          { bg:"var(--dashboard-blue-soft, #E6F1FB)", color:"var(--dashboard-blue, #185FA5)" },
                          { bg:"var(--dashboard-green-soft, #E1F5EE)", color:"var(--dashboard-green, #0F6E56)" },
                          { bg:"var(--dashboard-amber-soft, #FAEEDA)", color:"var(--dashboard-amber, #854F0B)" },
                          { bg:"var(--dashboard-blue-soft, #EEEDFE)", color:"var(--dashboard-blue, #534AB7)" },
                          { bg:"var(--dashboard-red-soft, #FAECE7)", color:"var(--dashboard-red, #993C1D)" }
                        ];

                        const av = avatarColors[i % avatarColors.length];

                        return (
                          <React.Fragment key={c.id||i}>
                            {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
                            <button
                              key={c.id||i}
                              onClick={() => onSelectCandidate?.(c)}
                              style={{
                                display:"flex",
                                alignItems:"center",
                                gap:12,
                                padding:"12px 14px",
                                borderRadius:12,
                                marginBottom:8,
                                width:"100%",
                                border:`1px solid ${C.border}`,
                                background:"var(--dashboard-card, #fff)",
                                cursor:"pointer",
                                textAlign:"left",
                                transition:"all 0.2s ease",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = "var(--dashboard-hover, #F5F3FF)"; e.currentTarget.style.borderColor = "var(--dashboard-blue-border, #C7D2FE)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "var(--dashboard-card, #fff)"; e.currentTarget.style.borderColor = "var(--dashboard-border, #ECEEF8)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                            >
                              <div style={{ width:40, height:40, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:14, fontWeight:500, flexShrink:0 }}>
                                {(c.name||c.candidate_name||"C")[0].toUpperCase()}
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:14, fontWeight:500, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", marginBottom:4 }}>{c.name||c.candidate_name||"Candidate"}</div>
                                <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                                  {c.location ? (
                                    <MetaPill type="loc" icon={<PinIcon />}>{c.location}</MetaPill>
                                  ) : null}
                                  {c.source || c.platform ? (
                                    <MetaPill type={c.source?.toLowerCase().includes("naukri") || c.platform?.toLowerCase().includes("naukri") ? "naukri" : "src"}>{c.source || c.platform}</MetaPill>
                                  ) : null}
                                </div>
                              </div>
                            </button>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <div style={{ padding:"20px", textAlign:"center", color:C.muted, fontSize:12 }}>No candidates found</div>
                    )}

                    {pagination.totalPages > 1 && !isLoading && (
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:12, paddingTop:12, borderTop:`1px solid ${C.border}` }}>
                        <button
                          onClick={() => handlePageChange(sid, pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                          style={{
                            padding:"6px 12px",
                            borderRadius:8,
                            border:pagination.currentPage === 1 ? `1px solid ${C.border}` : `1px solid ${C.blue}`,
                            background:pagination.currentPage === 1 ? C.surface : C.blue,
                            color:pagination.currentPage === 1 ? C.muted : "#fff",
                            fontSize:12,
                            fontWeight:500,
                            cursor:pagination.currentPage === 1 ? "default" : "pointer",
                            transition:"all 0.2s"
                          }}
                        >
                          Prev
                        </button>
                        <span style={{ fontSize:12, color:C.muted, fontWeight:500 }}>
                          {pagination.currentPage} / {pagination.totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(sid, pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                          style={{
                            padding:"6px 12px",
                            borderRadius:8,
                            border:pagination.currentPage === pagination.totalPages ? `1px solid ${C.border}` : `1px solid ${C.blue}`,
                            background:pagination.currentPage === pagination.totalPages ? C.surface : C.blue,
                            color:pagination.currentPage === pagination.totalPages ? C.muted : "#fff",
                            fontSize:12,
                            fontWeight:500,
                            cursor:pagination.currentPage === pagination.totalPages ? "default" : "pointer",
                            transition:"all 0.2s"
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}

                  </div>

                )}

              </div>

            );

          }).filter(Boolean)

        }

      </div>

    </div>

  );

}



/* — Today's Tasks — */

function TodayTasksPanel() {

  const [tasks, setTasks] = useState(null);



  useEffect(() => {

    (async () => {

      const [followups, interviews, pending] = await Promise.allSettled([

        aiAutomationApi.get("/outreach/followups/today"),

        aiAutomationApi.get("/interviews/today"),

        aiAutomationApi.get("/automation_candidates/pending-actions"),

      ]);

      setTasks({

        followups:  followups.value?.data||[],

        interviews: interviews.value?.upcoming_interviews||[],

        pending:    pending.value?.data||[],

      });

    })();

  }, []);



  const sections = [

    { key:"followups", label:"Follow-ups", color:C.amber, bg:C.amberLt, border:"var(--dashboard-amber-border, #FDE68A)", empty:"No follow-ups due today." },

    { key:"interviews", label:"Interviews Today", color:C.blue, bg:C.blueLt, border:C.blueMid, empty:"No interviews scheduled for today." },

    { key:"pending", label:"Pending Actions", color:C.green, bg:C.greenLt, border:"var(--dashboard-green-border, #A7F3D0)", empty:"No pending actions for today." },

  ];



  return (

    <div style={{ flex:1, overflowY:"auto", padding:"6px 10px" }}>

      {tasks===null ? <Loader /> : sections.map(s => {

        const items = tasks[s.key]||[];

        return (

          <div key={s.key}>

            <div style={{ padding:"8px 12px", background:s.bg, display:"flex", alignItems:"center", justifyContent:"space-between", borderRadius:10, marginBottom:8, border:`1px solid ${s.border}` }}>

              <span style={{ fontSize:11, fontWeight:700, color:s.color }}>{s.label}</span>

              <span style={{ background:"var(--dashboard-card, #fff)", color:s.color, borderRadius:"50%", width:18, height:18, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:800, border:`1px solid ${s.border}`, boxShadow:"0 4px 10px rgba(15, 23, 42, 0.12)" }}>{items.length}</span>

            </div>

            {items.length===0 ? <div style={{ padding:"8px 12px 12px", fontSize:12, color:C.muted }}>{s.empty}</div> :

              items.slice(0,5).map((item,i) => {
                const avatarColors = [
                  { bg:C.blueLt, color:C.blue },
                  { bg:C.greenLt, color:C.green },
                  { bg:C.amberLt, color:C.amber },
                  { bg:C.blueLt, color:C.blue },
                  { bg:C.redLt, color:C.red }
                ];

                const av = avatarColors[i % avatarColors.length];

                return (
                  <React.Fragment key={i}>
                    {i > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
                    <div style={{
                      display:"flex",
                      alignItems:"center",
                      gap:12,
                      padding:"10px 12px",
                      borderRadius:12,
                      marginBottom:6,
                      border:`1px solid ${C.border}`,
                      cursor:"pointer",
                      background:"var(--dashboard-card, #fff)",
                      transition:"all 0.2s ease",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = "var(--dashboard-hover, #F5F3FF)"; e.currentTarget.style.borderColor = "var(--dashboard-blue-border, #C7D2FE)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "var(--dashboard-card, #fff)"; e.currentTarget.style.borderColor = "var(--dashboard-border, #ECEEF8)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                    >
                      <div style={{ width:36, height:36, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:14, fontWeight:500, flexShrink:0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/></svg>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.name||item.candidate_name||item.title||"Task"}</p>
                        <p style={{ fontSize:12, color:C.muted, margin:"3px 0 0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.time||item.scheduled_time||item.due_date||item.job_title||item.candidate_email||"Action item"}</p>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })

            }

          </div>

        );

      })}

    </div>

  );


}


/* — Meeting Notes — */

function MeetingNotesPanel() {

  const [email, setEmail]   = useState("");

  const [loading, setLoading] = useState(false);

  const [notes, setNotes]   = useState([]);

  const [selected, setSelected] = useState(null);



  const load = async () => {

    if (!email.trim()) return;

    setLoading(true);

    try {

      const data = await aiAutomationApi.get(`/interviews/candidate/${encodeURIComponent(email.trim())}/notes`);

      const rows = data?.notes||data||[]; setNotes(Array.isArray(rows)?rows:[]); setSelected(null);

    } catch { setNotes([]); } finally { setLoading(false); }

  };



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <div style={{ padding:"12px 14px", borderBottom:`1px solid ${C.border}` }}>

        <div style={{ display:"flex", gap:8 }}>

          <input value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") load(); }}

            placeholder="candidate@email.com" style={{ flex:1, borderRadius:10, border:`1px solid ${C.border}`, padding:"8px 12px", fontSize:12, outline:"none", background:C.surface, color:C.text }} />

          <button onClick={load} style={{ background:C.blue, color:"#fff", border:"none", borderRadius:10, padding:"8px 14px", fontSize:12, fontWeight:600, cursor:"pointer" }}>{loading?"…":"Load"}</button>

        </div>

      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"8px 12px" }}>

        {notes.length===0 && !loading && email && <Empty text="No notes found" />}

        {notes.map((n,idx) => {
          const avatarColors = [
            { bg:"#E6F1FB", color:"#185FA5" },
            { bg:"#E1F5EE", color:"#0F6E56" },
            { bg:"#FAEEDA", color:"#854F0B" },
            { bg:"#EEEDFE", color:"#534AB7" },
            { bg:"#FAECE7", color:"#993C1D" }
          ];

          const av = avatarColors[idx % avatarColors.length];

          return (
            <React.Fragment key={n.interview_id||idx}>
              {idx > 0 && <div style={{ height:"0.5px", background:C.border, margin:"0 12px" }} />}
              <button 
                key={n.interview_id||idx} 
                onClick={() => setSelected(n)} 
                style={{
                  width:"100%", 
                  display:"flex",
                  alignItems:"center",
                  gap:12,
                  padding:"12px",
                  borderRadius:12,
                  border:selected?.interview_id===n.interview_id?"0.5px solid "+C.blue:"0.5px solid transparent",
                  cursor:"pointer",
                  background:selected?.interview_id===n.interview_id?"#F7F8FF":"transparent",
                  transition:"background 0.12s, border-color 0.12s",
                  textAlign:"left"
                }}
              >
                <div style={{ width:38, height:38, borderRadius:"50%", background:av.bg, display:"flex", alignItems:"center", justifyContent:"center", color:av.color, fontSize:13, fontWeight:500, flexShrink:0 }}>{idx+1}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:14, fontWeight:500, color:C.text, margin:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>Interview {n.interview_id||idx+1}</p>
                  <p style={{ fontSize:12, color:C.muted, margin:"3px 0 0" }}>{n.ai_note_taker_status||"completed"}</p>
                </div>
              </button>
            </React.Fragment>
          );
        })}

      </div>

      {selected && (

        <div style={{ borderTop:`1px solid ${C.border}`, padding:14, background:"var(--dashboard-surface, #fafbff)" }}>

          <p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:".5px", color:C.muted, marginBottom:6 }}>Executive Summary</p>

          <p style={{ fontSize:12, color:C.text, lineHeight:1.65, margin:0 }}>{selected?.notes?.executive_summary||"No summary available."}</p>

        </div>

      )}


    </div>

  );

}



/* — Manage Campaign Panel — */

function ManageCampaignPanel({ onShowChat, initialSelectedSessionId, onOpenSessionPopup, onOpenResumeUpload }) {

  const navigate = useNavigate();

  const [sessions, setSessions] = useState(null);

  const [selectedSessionId, setSelectedSessionId] = useState(initialSelectedSessionId || "");

  const [chatHistory, setChatHistory] = useState([]);

  const [historyLoading, setHistoryLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const [sessionDropdownOpen, setSessionDropdownOpen] = useState(false);

  const [sessionSearchQuery, setSessionSearchQuery] = useState("");

  const sessionDropdownRef = useRef(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const refreshSessions = () => setRefreshKey(prev => prev + 1);



  useEffect(() => {

    aiAutomationApi.get("/sessions?limit=50")

      .then(r => { const rows = r?.sessions||r?.data||r||[]; setSessions(Array.isArray(rows)?rows.map(s=>({...s,_label:resolveSessionTitle(s)})):[]) })

      .catch(() => setSessions([]));

  }, [refreshKey]);



  useEffect(() => {

    if (initialSelectedSessionId && initialSelectedSessionId !== selectedSessionId) {

      setSelectedSessionId(initialSelectedSessionId);

    }

  }, [initialSelectedSessionId]);



  useEffect(() => {

    if (!selectedSessionId) return;

    setHistoryLoading(true);

    aiAutomationApi.get(`/sessions/${selectedSessionId}/chat-messages`)

      .then(res => {

        const msgs = Array.isArray(res?.messages) ? res.messages : [];

        const transformed = msgs.map((m, idx) => ({

          id: m.id || `${selectedSessionId}_${idx}`,

          sender: (m.message_type || m.message_role || "").toLowerCase() === "user" ? "user" : "agent",

          message: m.content || m.text || "",

          timestamp: m.created_at || m.timestamp || new Date().toISOString(),

        }));

        setChatHistory(transformed);

      })

      .catch(err => { console.error("Error loading messages:", err); setChatHistory([]); })

      .finally(() => setHistoryLoading(false));

  }, [selectedSessionId]);



  // Handle user query

  const handleUserQuery = async (query) => {

    if (!selectedSessionId) return;

    try {

      await aiAutomationApi.post("/chat", { message: query, session_id: selectedSessionId });

      const res = await aiAutomationApi.get(`/sessions/${selectedSessionId}/chat-messages`);

      const msgs = Array.isArray(res?.messages) ? res.messages : [];

      const transformed = msgs.map((m, idx) => ({

        id: `msg-${selectedSessionId}-${idx}`,

        sender: (m.message_type || m.message_role || "").toLowerCase() === "user" ? "user" : "agent",

        message: m.content || m.text || "",

        timestamp: m.created_at || m.timestamp || new Date().toISOString(),

      }));

      setChatHistory(transformed);

    } catch (err) {

      console.error("Failed to send message:", err);

    }

  };



  const filteredSessions = (sessions||[]).filter(s => {

    if (!searchQuery.trim()) return true;

    const label = (s._label||"").toLowerCase();

    return label.includes(searchQuery.toLowerCase());

  });



  if (!selectedSessionId) {

    return (

      <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

        <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>

          <p style={{ fontSize:11, color:C.muted, margin:0 }}>AI-powered sourcing campaigns — LinkedIn & Naukri</p>

          <div style={{ position:"relative", flex:1 }}>

            <svg style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)" }} width="13" height="13" viewBox="0 0 16 16" fill="none">

              <circle cx="7" cy="7" r="5" stroke={C.muted} strokeWidth="1.3"/>

              <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round"/>

            </svg>

            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search sessions..." style={{

              width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:20,

              padding:"7px 14px 7px 30px", fontSize:12, outline:"none", color:C.text, boxSizing:"border-box",

            }}/>

          </div>

          <button onClick={refreshSessions} style={{ display:"flex", alignItems:"center", gap:4, background:C.surface, color:C.text, border:`1px solid ${C.border}`, borderRadius:8, padding:"6px 10px", fontSize:11, fontWeight:600, cursor:"pointer" }}>

            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>

          </button>

        </div>

        <div style={{ flex:1, overflowY:"auto", padding:14 }}>

          {sessions===null ? <Loader /> : filteredSessions.length===0 ? (
            <div style={{ textAlign:"center", padding:"40px 20px" }}>
              <p style={{ fontSize:14, fontWeight:600, color:C.text, margin:"0 0 8px" }}>Start the hiring process from here</p>
              <button
                onClick={() => onOpenSessionPopup?.()}
                style={{
                  padding:"10px 30px", borderRadius:8, fontSize:13, fontWeight:600,
                  background:"linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)", color:"#fff", border:"none", cursor:"pointer",
                  width:"fit-content"
                }}
              >
                Create JD
              </button>
            </div>
          ) : filteredSessions.map(s => (

              <div key={s.id} style={{ borderRadius:12, border:`1px solid ${C.border}`, marginBottom:8, overflow:"hidden", cursor:"pointer", background:"var(--dashboard-card, #fff)", transition:"all 0.2s ease" }} onClick={() => navigate(`/chat?session_id=${s.id}&from_dashboard=true`)} onMouseEnter={e => { e.currentTarget.style.borderColor = C.blueMid; e.currentTarget.style.background = "var(--dashboard-hover, #EEF2FF)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79, 70, 229, 0.1)"; }} onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = "var(--dashboard-card, #fff)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>

                <div style={{ padding:"14px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                    <p style={{ fontSize:14, fontWeight:600, color:C.text, margin:0, flex:1, marginRight:8, wordBreak:"break-word", whiteSpace:"normal" }}>{s._label||`Session ${s.id}`}</p>
                    <Badge status={s.status||"active"} />
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                    <div style={{ padding:"6px 12px", borderRadius:20, background:C.blueLt, color:C.blue, fontSize:11, fontWeight:600, border:`0.5px solid ${C.blueMid}` }}>
                      {s.candidate_count||s.sourced_count||0} Sourced
                    </div>
                    <div style={{ padding:"6px 12px", borderRadius:20, background:C.greenLt, color:C.green, fontSize:11, fontWeight:600, border:"0.5px solid var(--dashboard-green-border, #A8D5BA)" }}>
                      {s.outreach_count||0} Outreach
                    </div>
                    <div style={{ padding:"6px 12px", borderRadius:20, background:C.amberLt, color:C.amber, fontSize:11, fontWeight:600, border:"0.5px solid var(--dashboard-amber-border, #FAC775)" }}>
                      {s.reply_count||0} Replies
                    </div>
                  </div>
                </div>

              </div>

            ))

          }

        </div>

      </div>

    );

  }



  return (

    <div style={{ display:"flex", flexDirection:"column", height:"100%" }}>

      <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>

        <p style={{ fontSize:11, color:C.muted, margin:0 }}>AI-powered sourcing campaigns — LinkedIn & Naukri</p>

        <div ref={sessionDropdownRef} style={{ position:"relative" }}>

          <button

            onClick={() => setSessionDropdownOpen(!sessionDropdownOpen)}

            style={{ 

              padding:"6px 12px", borderRadius:8, border:`1px solid ${C.border}`, 

              background:C.surface, color:C.text, fontSize:12, outline:"none", cursor:"pointer",

              display:"flex", alignItems:"center", gap:6, minWidth:180, justifyContent:"space-between"

            }}

          >

            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>

              {selectedSessionId ? (sessions?.find(s => s.id === selectedSessionId)?._label || `Session ${selectedSessionId}`) : "Choose a session…"}

            </span>

            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" style={{ transform:sessionDropdownOpen?"rotate(180deg)":"none", transition:"transform .2s" }}>

              <path d="M19 9l-7 7-7-7"/>

            </svg>

          </button>



          {sessionDropdownOpen && (

            <div style={{ 

              position:"absolute", top:"100%", left:0, marginTop:6, 

              width:"100%", minWidth:250, maxHeight:320, 

              background:"var(--dashboard-card, #fff)", borderRadius:8, border:`1px solid ${C.border}`,

              boxShadow:"0 4px 12px rgba(0,0,0,0.1)", zIndex:100, overflow:"hidden"

            }}>

              <div style={{ padding:8, borderBottom:`1px solid ${C.border}` }}>

                <div style={{ position:"relative" }}>

                  <svg style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)" }} width="12" height="12" viewBox="0 0 16 16" fill="none">

                    <circle cx="7" cy="7" r="5" stroke={C.muted} strokeWidth="1.3"/>

                    <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round"/>

                  </svg>

                  <input

                    value={sessionSearchQuery}

                    onChange={e => setSessionSearchQuery(e.target.value)}

                    placeholder="Search sessions..."

                    style={{

                      width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:6,

                      padding:"6px 10px 6px 28px", fontSize:11, outline:"none", color:C.text, boxSizing:"border-box",

                    }}

                  />

                </div>

              </div>

              <div style={{ overflowY:"auto", maxHeight:250 }}>

                {sessions===null ? (

                  <div style={{ padding:20, textAlign:"center", color:C.muted, fontSize:11 }}>Loading…</div>

                ) : sessions.length === 0 ? (

                  <div style={{ padding:20, textAlign:"center", color:C.muted, fontSize:11 }}>No sessions found</div>

                ) : (

                  sessions

                    .filter(s => {

                      if (!sessionSearchQuery.trim()) return true;

                      return (s._label || '').toLowerCase().includes(sessionSearchQuery.toLowerCase());

                    })

                    .map(s => (

                      <button

                        key={s.id}

                        onClick={() => {

                          setSelectedSessionId(s.id);

                          setSessionDropdownOpen(false);

                          setSessionSearchQuery('');

                        }}

                        style={{

                          width:"100%", padding:"8px 12px", background:"transparent", border:"none",

                          textAlign:"left", fontSize:11, color:C.text, cursor:"pointer",

                          display:"flex", alignItems:"center", gap:8,

                          hoverBackground: "#f7f8ff"

                        }}

                        onMouseEnter={e => e.currentTarget.style.background="var(--dashboard-hover, #f7f8ff)"}

                        onMouseLeave={e => e.currentTarget.style.background="transparent"}

                      >

                        <div style={{ flex:1, minWidth:0 }}>

                          <div style={{ fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>

                            {s._label || `Session ${s.id}`}

                          </div>

                          <div style={{ fontSize:9, color:C.muted, marginTop:2 }}>

                            {s.candidate_count || s.sourced_count || 0} candidates

                          </div>

                        </div>

                      </button>

                    ))

                )}

              </div>

            </div>

          )}

        </div>

      </div>

      <div style={{ flex:1, overflow:"hidden" }}>

        <ChatMain

          selectedJobId={selectedSessionId}

          onUserQuery={handleUserQuery}

          initialMessages={chatHistory}

          historyLoading={historyLoading}

          showHeader={false}

          onAttachmentClick={onOpenResumeUpload}

        />

      </div>

    </div>

  );

}



/* — Chat Panel (Campaign List) — */

function ChatPanel({ sessionId: initialSessionId, onSwitchPanel, onSelectChatSession, onOpenSessionPopup }) {

  const [sessions, setSessions] = useState(null);

  const navigate = useNavigate();



  useEffect(() => {

    aiAutomationApi.get("/sessions?limit=50")

      .then(r => { const rows = r?.sessions||r?.data||r||[]; setSessions(Array.isArray(rows)?rows.map(s=>({...s,_label:resolveSessionTitle(s)})):[]) })

      .catch(() => setSessions([]));

  }, []);



  const handleCardClick = (session) => {

    onSelectChatSession?.(session.id);

    onSwitchPanel(P.MANAGE_CAMPAIGN);

  };



  return (

    <div style={{ flex:1, overflowY:"auto", padding:14 }}>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>

        <p style={{ fontSize:11, color:C.muted, margin:0 }}>AI-powered sourcing campaigns — LinkedIn & Naukri</p>

        <div style={{ display:"flex", gap:8 }}>

          <button onClick={() => onSwitchPanel(P.CHAT)} style={{ display:"flex", alignItems:"center", gap:5, background:C.surface, color:C.text, border:`1px solid ${C.border}`, borderRadius:9, padding:"6px 14px", fontSize:11, fontWeight:600, cursor:"pointer" }}>

            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>

            Pipeline Overview

          </button>

          <button onClick={() => onSwitchPanel(P.MANAGE_CAMPAIGN)} style={{ display:"flex", alignItems:"center", gap:5, background:C.blue, color:"#fff", border:"none", borderRadius:9, padding:"6px 14px", fontSize:11, fontWeight:600, cursor:"pointer" }}>

            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>

            Manage Campaign

          </button>

        </div>

      </div>

      {sessions===null ? <Loader /> : sessions.length===0 ? (
        <div style={{ textAlign:"center", padding:"40px 20px" }}>
          <p style={{ fontSize:14, fontWeight:600, color:C.text, margin:"0 0 8px" }}>Start the hiring process from here</p>
          <button
            onClick={() => onOpenSessionPopup?.()}
            style={{
              padding:"10px 20px", borderRadius:8, fontSize:13, fontWeight:600,
              background:C.blue, color:"#fff", border:"none", cursor:"pointer"
            }}
          >
            Create JD
          </button>
        </div>
      ) : 

        sessions.map(s => (

          <div key={s.id} style={{ borderRadius:12, border:`1px solid ${C.border}`, marginBottom:10, overflow:"hidden", cursor:"pointer" }} onClick={() => handleCardClick(s)}>

            <div style={{ padding:"12px 14px", background:C.surface, display:"flex", alignItems:"center", justifyContent:"space-between" }}>

              <p style={{ fontSize:13, fontWeight:600, color:C.text, margin:0, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", flex:1, marginRight:8 }}>{s._label||`Session ${s.id}`}</p>

              <Badge status={s.status||"active"} />

            </div>

            <div style={{ padding:"10px 14px", display:"flex", gap:20 }}>

              <div><p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:C.muted, marginBottom:2 }}>Sourced</p><p style={{ fontSize:16, fontWeight:700, color:C.blue, margin:0 }}>{s.candidate_count||s.sourced_count||0}</p></div>

              <div><p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:C.muted, marginBottom:2 }}>Outreach</p><p style={{ fontSize:16, fontWeight:700, color:C.green, margin:0 }}>{s.outreach_count||0}</p></div>

              <div><p style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", color:C.muted, marginBottom:2 }}>Replies</p><p style={{ fontSize:16, fontWeight:700, color:C.amber, margin:0 }}>{s.reply_count||0}</p></div>

            </div>

            <div style={{ padding:"10px 14px", borderTop:`1px solid ${C.border}`, background:"var(--dashboard-surface, #fafbff)" }}>

              <button 

                onClick={(e) => { e.stopPropagation(); navigate(`/chat?session_id=${s.id}&from_dashboard=true`); }}

                style={{ width:"100%", padding:"8px 12px", background:C.blueLt, color:C.blue, border:`1px solid ${C.blueMid}`, borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer", textAlign:"center" }}

              >

                Do More Action

              </button>

            </div>

          </div>

        ))

      }

    </div>

  );

}



function RightPanel({ open, width, panel, onClose, user, onSelectCandidate, onShowResume, onShowInterview, onShowReschedule, onSwitchPanel, onSelectChatSession, selectedChatSessionId, uploadFileLogs, setUploadFileLogs, isParsingActive, setIsParsingActive, setShowSessionPopup, onRefreshStats }) {

  const meta = PANEL_META[panel] || { title:"Panel", sub:"", icon:"📋", color:"#3B5BDB", bg:"#EEF2FF" };

  const panelShell = {
    background: "var(--dashboard-panel-gradient, linear-gradient(180deg, #FFFFFF 0%, #F5F8FC 100%))",
    borderLeft: "1px solid var(--dashboard-border, #D7E0EE)",
    boxShadow: "-18px 0 40px rgba(15, 23, 42, 0.06)",
  };

  const panelHeader = {
    background: "var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))",
    borderBottom: "1px solid var(--dashboard-border, #E2E8F2)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  };

  // Navigation panels for quick switching
  const navPanels = [P.RESUME_UPLOAD, P.MASTER_DATABASE, P.MY_INTERVIEWS, P.RESCHEDULE, P.MANAGE_CAMPAIGN];

  return (

    <div style={{
      width: open ? `${width}%` : 0, flexShrink:0, display:"flex", flexDirection:"column",
      overflow:"hidden", transition:"width .28s cubic-bezier(.16,1,.3,1)",
      ...panelShell,
    }}>

      {open && (

        <>

          {/* Panel Header */}
          <div style={{ padding:"16px 16px", display:"flex", alignItems:"center", gap:10, flexShrink:0, ...panelHeader }}>

            <div style={{ width:34, height:34, borderRadius:12, background:"var(--dashboard-hover, #EEF2FF)", border:`1px solid ${meta.color}40`, boxShadow:`0 8px 18px ${meta.color}14`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:14 }}>

              {meta.icon}

            </div>

            <div style={{ flex:1, minWidth:0 }}>

              <p style={{ fontSize:13, fontWeight:700, color:C.text, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", letterSpacing:"0.01em" }}>{meta.title}</p>

              <p style={{ fontSize:10, color:C.muted, margin:"2px 0 0" }}>{meta.sub}</p>

            </div>

            {/* Navigation Pills */}
            {/* <div style={{ display:"flex", gap:4 }}>

              {navPanels.map(p => {

                const pMeta = PANEL_META[p];

                return (

                  <button

                    key={p}

                    onClick={() => onSwitchPanel(p)}

                    title={pMeta?.title || p}

                    style={{

                      width:26, height:26, borderRadius:7, border: panel===p ? `1px solid ${meta.color}40` : "1px solid #E5E7EB",

                      background: panel===p ? "var(--dashboard-hover, #EEF2FF)" : "transparent", cursor:"pointer", fontSize:12,

                      display:"flex", alignItems:"center", justifyContent:"center", transition:"all .12s"

                    }}

                  >

                  </button>

                );

              })}

            </div> */}

            <button onClick={onClose} style={{ width:28, height:28, borderRadius:9, border:`1px solid ${C.border}`, background:"var(--dashboard-card, #FFFFFF)", boxShadow:"0 6px 14px rgba(15, 23, 42, 0.05)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:C.muted, transition:"all .12s" }}>

              <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>

            </button>

          </div>

          {/* Search Bar - Hide where panel-specific search/UI already exists */}
          {panel !== P.RESCHEDULE && panel !== P.SHOW_CANDIDATES && panel !== P.MANAGE_CAMPAIGN && panel !== P.RESUME_UPLOAD && panel !== P.MASTER_DATABASE && (
            <div style={{ padding:"8px 10px 10px", borderBottom:`1px solid ${C.border}`, background:"var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))" }}>

              <div style={{ position:"relative" }}>

                <svg style={{ position:"absolute", left:11, top:"50%", transform:"translateY(-50%)" }} width="12" height="12" viewBox="0 0 16 16" fill="none">

                  <circle cx="7" cy="7" r="5" stroke="#9CA3AF" strokeWidth="1.3"/>

                  <path d="M11 11l3 3" stroke="#9CA3AF" strokeWidth="1.3" strokeLinecap="round"/>

                </svg>

                <input

                  placeholder="Search…"

                  style={{

                    width:"100%", background:"var(--dashboard-card, #FFFFFF)", border:`1px solid ${C.border}`, borderRadius:20,

                    padding:"8px 12px 8px 28px", fontSize:12, outline:"none", color:C.text, fontFamily:"inherit",
                    boxShadow:"0 8px 18px rgba(15, 23, 42, 0.04)",

                  }}

                />

              </div>

            </div>
          )}

          {/* Content Area */}
          <div style={{ display:"flex", flexDirection:"column", flex:1, overflow:"hidden", minHeight:0, background:"var(--dashboard-content-gradient, linear-gradient(180deg, #F8FAFD 0%, #F3F7FC 100%))" }}>

            {panel===P.RESUME_UPLOAD      ? <DashboardResumeUploadPanel user={user} onShowResume={onShowResume} uploadFileLogs={uploadFileLogs} setUploadFileLogs={setUploadFileLogs} isParsingActive={isParsingActive} setIsParsingActive={setIsParsingActive} onRefreshStats={onRefreshStats} />

            : panel===P.MASTER_DATABASE    ? <MasterDatabasePanel onSelectCandidate={onSelectCandidate} />

            : panel===P.MY_INTERVIEWS      ? <MyInterviewsPanel onShowInterview={onShowInterview} />

            : panel===P.RESCHEDULE         ? <ReschedulePanel onShowReschedule={onShowReschedule} />

            : panel===P.NOTIFICATIONS      ? <NotificationsPanel />

            : panel===P.SHOW_CANDIDATES    ? <ShowCandidatesPanel onSelectCandidate={onSelectCandidate} />

            : panel===P.TODAY_TASKS        ? <TodayTasksPanel />

            : panel===P.MEETING_NOTES      ? <MeetingNotesPanel />

            : panel===P.MANAGE_CAMPAIGN    ? <ManageCampaignPanel onShowChat={() => onSwitchPanel(P.CHAT)} initialSelectedSessionId={selectedChatSessionId} onOpenSessionPopup={() => setShowSessionPopup(true)} onOpenResumeUpload={() => onSwitchPanel(P.RESUME_UPLOAD)} />

            : panel===P.CHAT               ? <ChatPanel onSwitchPanel={onSwitchPanel} onSelectChatSession={onSelectChatSession} onOpenSessionPopup={() => setShowSessionPopup(true)} />

            : <div style={{ padding:20, fontSize:12, color:C.muted }}>Select an option to get started.</div>

            }

          </div>

        </>

      )}


    </div>

  );

}

function TopBar({ user }) {

  const initials = (user?.name||"U").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase();

  return (

    <div style={{ height:56, background:"var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", padding:"0 18px", gap:10, flexShrink:0, boxShadow:"0 6px 20px rgba(15, 23, 42, 0.04)" }}>

      <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:14, fontWeight:600, color:C.blue, marginRight:"auto" }}>

        <div style={{ width:30, height:30, borderRadius:9, background:"var(--dashboard-hover, linear-gradient(135deg, #EAF2FF 0%, #FFFFFF 100%))", border:`1px solid ${C.blue}40`, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 8px 18px rgba(29, 78, 216, 0.10)" }}>

          <svg width="16" height="16" viewBox="0 0 20 20" fill="none">

            <circle cx="7"  cy="6" r="3" fill={C.blue}/>

            <path d="M1 16c0-3 2.7-5 6-5s6 2 6 5" stroke={C.blue} strokeWidth="1.5" strokeLinecap="round"/>

            <path d="M14 11c2.5 0 4 1.5 4 4"      stroke="#22c987" strokeWidth="1.5" strokeLinecap="round"/>

          </svg>

        </div>

        Wizbox

      </div>

      <div style={{ display:"flex", alignItems:"center", gap:7, padding:"4px 10px 4px 4px", borderRadius:16, border:`1px solid ${C.border}`, background:"var(--dashboard-card, #FFFFFF)", cursor:"pointer", boxShadow:"0 8px 18px rgba(15, 23, 42, 0.05)" }}>

        <div style={{ width:24, height:24, borderRadius:"50%", background:C.blue, color:"#fff", fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center" }}>{initials}</div>

        <div>

          <p style={{ fontSize:12, fontWeight:500, color:C.text, margin:0 }}>{user?.name||"User"}</p>

          <p style={{ fontSize:10, color:C.blue, margin:0 }}>{user?.role||"Recruiter"}</p>

        </div>

      </div>

    </div>

  );

}



/* ─────────────────────────────────────────────────────────────

   CONTEXTUAL BOT MESSAGES  (stats-aware)

───────────────────────────────────────────────────────────── */

function buildContextual(panelId, stats) {

  const s = stats;

  const map = {

    [P.RESUME_UPLOAD]:      { text: s.resumes===0 ? "📄 Upload your first resume to kick off the hiring process. Supported: PDF, DOC, DOCX." : `📄 You have ${s.resumes} resume${s.resumes!==1?"s":""} uploaded. Add more or explore your talent pool.`,

      actions: s.resumes===0 ? [] : [{ icon:"📄", label:VIEW_RESUME_LIBRARY_LABEL, action:P.RESUME_UPLOAD }, { icon:"🗄️", label:"View Master Database", action:P.MASTER_DATABASE }] },

    [P.MASTER_DATABASE]:    { text:"🗄️ Your master database shows all candidates. Click any row to view the full profile.",

      actions:[{ icon:"👥", label:"Show by Session", action:P.SHOW_CANDIDATES },] },

    [P.MY_INTERVIEWS]:      { text: s.interviews===0 ? "📅 No upcoming interviews yet. Schedule one to get started." : `📅 You have ${s.interviews} upcoming interview${s.interviews!==1?"s":""}. Click any row to view notes.`,

      actions: [{ icon:"📝", label:"Meeting Notes", action:P.MEETING_NOTES }] },

    [P.MEETING_NOTES]:      { text:"📝 Enter a candidate's email to load their interview notes and AI summary.", actions:[{ icon:"📅", label:"View Interviews", action:P.MY_INTERVIEWS }] },

    [P.RESCHEDULE]:         { text: s.reschedules===0 ? "🔄 No pending reschedule requests — you're all clear!" : `🔄 You have ${s.reschedules} reschedule request${s.reschedules!==1?"s":""} waiting. Review below.`,

      actions:[{ icon:"📅", label:"View Interviews", action:P.MY_INTERVIEWS }] },

    [P.NOTIFICATIONS]:      { text: s.notifications===0 ? "🔔 You're all caught up — no new notifications." : `🔔 You have ${s.notifications} notification${s.notifications!==1?"s":""}. Mark them as read when done.`,

      actions:[{ icon:"🔄", label:"Reschedule Requests", action:P.RESCHEDULE }] },

    [P.SHOW_CANDIDATES]:    { text:"👥 View candidates grouped by sourcing session. Click a row to see the full profile.", actions:[{ icon:"🗄️", label:"Master Database", action:P.MASTER_DATABASE }] },

    [P.TODAY_TASKS]:        { text:"✅ Here are your tasks for today — follow-ups, interviews, and pending actions.", actions:[{ icon:"📅", label:"My Interviews", action:P.MY_INTERVIEWS }] },

    [P.MANAGE_CAMPAIGN]:    { text:"🚀 Your AI-powered sourcing campaigns. Click any session to manage outreach.", actions:[{ icon:"👥", label:"Show Candidates", action:P.SHOW_CANDIDATES }] },

    [P.CANDIDATE_PROFILE]:  { text:"🔍 Search for a candidate by name, email, or role.", actions:[{ icon:"🗄️", label:"Master Database", action:P.MASTER_DATABASE }] },

    [P.CHAT]:               { text:"💬 View your AI-powered sourcing campaigns and their performance metrics.", actions:s.resumes > 0 ? [{ icon:"📄", label:VIEW_RESUME_LIBRARY_LABEL, action:P.RESUME_UPLOAD }, { icon:"🚀", label:"Manage Campaign", action:P.MANAGE_CAMPAIGN }] : [{ icon:"🚀", label:"Manage Campaign", action:P.MANAGE_CAMPAIGN }] },

  };

  return map[panelId] || null;

}



const CHAT_KEY = uid => `talentgpt_chat_v1_${uid}`;



/* ─────────────────────────────────────────────────────────────

   MAIN DASHBOARD PAGE

───────────────────────────────────────────────────────────── */

export default function DashboardPage() {

    const [showSessionPopup, setShowSessionPopup] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { canUseFeature } = useCompanyFeatureAccess();

  const user = useMemo(() => getUserInfo(), []);



  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const [stats,          setStats]          = useState({ resumes:0, interviews:0, reschedules:0, notifications:0, jds:0, campaigns:0 });

  const [statsLoaded,    setStatsLoaded]    = useState(false);



  const [messages,  setMessages]  = useState([]);

  const [input,     setInput]     = useState("");

  const [thinking,  setThinking]  = useState(false);



  const [panelOpen,    setPanelOpen]    = useState(false);

  const [panelWidth,   setPanelWidth]   = useState(38);

  const [panel,        setPanel]        = useState(P.MASTER_DATABASE);

  const [selectedChatSessionId, setSelectedChatSessionId] = useState(null);

  const [isResizing,   setIsResizing]   = useState(false);

  const [uploadFileLogs, setUploadFileLogs] = useState([]);

  const [isParsingActive, setIsParsingActive] = useState(false);

  const chatEndRef = useRef(null);

  const inputRef   = useRef(null);

  const containerRef = useRef(null);



  /* auth guard */

  useEffect(() => {

    const token = localStorage.getItem("auth_token");

    if (!token) { navigate("/auth/login"); return; }

    setIsCheckingAuth(false);
  }, [navigate]);


  /* load real stats function */
  const loadDashboardStats = useCallback(async () => {
    try {
      const m = getMetadata(); const cid = m?.cid;
      const [interviews, reschedules, notifications, resumes, jds, masterDb, masterDbCount, sessions] = await Promise.allSettled([
        aiAutomationApi.get(`/interviews/upcoming${cid ? `?cid=${cid}` : ""}`),
        aiAutomationApi.get(`/interviews/reschedule-requests?status=pending${cid?`&cid=${cid}`:""}`),
        aiAutomationApi.get(`/interviews/action-notifications${cid?`?cid=${cid}`:""}`),
        coreApi.post("/resume-parsing/list", { cid, userId: m?.uid||m?.userId||m?.id, page:1 }),
        aiAutomationApi.get(`/job-descriptions${cid?`?cid=${cid}`:""}`),
        aiAutomationApi.get("/automation_candidates/all?limit=200"),
        aiAutomationApi.get("/automation_candidates/all?limit=1"),
        aiAutomationApi.get("/sessions?limit=200"),
      ]);
      console.log("Master DB API response:", masterDb);
      console.log("Master DB Count response:", masterDbCount);
      const sessionRows = sessions.status === 'fulfilled' ? (Array.isArray(sessions.value?.sessions||sessions.value?.data||sessions.value) ? (sessions.value?.sessions||sessions.value?.data||sessions.value) : []) : [];
      const newStats = {
        resumes:       resumes.value?.data?.total || resumes.value?.data?.resumes?.length || 0,
        interviews:    interviews.value?.upcoming_interviews?.length ?? 0,
        reschedules:   (Array.isArray(reschedules.value) ? reschedules.value : []).length,
        notifications: notifications.value?.notifications?.length ?? 0,
        jds:           (Array.isArray(jds.value?.data) ? jds.value.data : jds.value?.job_descriptions || []).length,
        masterDb:      masterDbCount.value?.total_records || masterDbCount.value?.total || masterDbCount.value?.count || masterDb.value?.total_records || masterDb.value?.total || masterDb.value?.count || (Array.isArray(masterDb.value?.data||masterDb.value?.candidates||masterDb.value) ? (masterDb.value?.data||masterDb.value?.candidates||masterDb.value).length : 0),
        campaigns:     sessionRows.length,
      };
      setStats(newStats);
      setStatsLoaded(true);
    } catch (error) {
      console.error("Error loading stats:", error);
      setStatsLoaded(true);
    }
  }, []);

  /* load real stats once */

  useEffect(() => {

    if (isCheckingAuth) return;

    (async () => {

      try {

        await loadDashboardStats();

        /* restore chat history on load */

        try {

          const stored = JSON.parse(localStorage.getItem(CHAT_KEY(user.id))||"[]");

          if (stored.length > 0) {

            const hasOnboarding = stored.some(m => m.type==="onboarding");

            setMessages(stored.map(m=>({...m,ts:m.ts?new Date(m.ts):new Date()})));

            if (!hasOnboarding) {

              setMessages(prev => [{ id:1, from:"bot", type:"onboarding", ts:new Date() }, ...prev]);

            }

          } else {

            /* inject onboarding message only - OnboardingCard shows contextual info */

            setMessages([{ id:1, from:"bot", type:"onboarding", ts:new Date() }]);

          }

        } catch (error) {

          console.error("Error restoring chat history:", error);

          setMessages([{ id:1, from:"bot", type:"onboarding", ts:new Date() }]);

        }

      } catch (error) {

        console.error("Error loading stats:", error);

        setStatsLoaded(true);

      }

    })();

  }, [isCheckingAuth, loadDashboardStats]);


  useEffect(() => {

    if (!user?.id || messages.length===0) return;

    localStorage.setItem(CHAT_KEY(user.id), JSON.stringify(messages.filter(m=>m.type!=="onboarding").slice(-120)));

  }, [messages, user?.id]);



  useEffect(() => { 

    chatEndRef.current?.scrollIntoView({ behavior:"smooth" }); 

  }, [messages, thinking]);



  /* resize */

  const startResize = useCallback(e => { e.preventDefault(); setIsResizing(true); }, []);

  useEffect(() => {

    if (!isResizing) return;

    const onMove = e => { if (!containerRef.current) return; const r = containerRef.current.getBoundingClientRect(); setPanelWidth(Math.min(70, Math.max(25, ((r.right-e.clientX)/r.width)*100))); };

    const onUp = () => setIsResizing(false);

    document.addEventListener("mousemove", onMove);

    document.addEventListener("mouseup", onUp);

    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };

  }, [isResizing]);



  /* helpers */

  const addMsg = useCallback((from, text, actions=[]) => {

    setMessages(p => [...p, { id:Date.now()+Math.random(), from, type:"text", text, actions, ts:new Date() }]);

  }, []);



  const openPanel = useCallback(id => {

    if (id === 'create_jd') {

      setShowSessionPopup(true);

      return;

    }

    const requiredFeature = PANEL_FEATURE_ACCESS[id];
    if (requiredFeature && !canUseFeature(requiredFeature)) {
      addMsg("bot", getFeatureBlockedMessage(requiredFeature));
      return;
    }

    setPanel(id); setPanelOpen(true);

    setThinking(true);

    setTimeout(() => {

      setThinking(false);

      const ctx = buildContextual(id, stats);

      if (ctx) addMsg("bot", ctx.text, ctx.actions);

    }, 320);

  }, [addMsg, canUseFeature, stats]);

  // Handler for session popup selection

  const handleSessionSelect = useCallback(sid => {

    setShowSessionPopup(false);

    if (sid === 'new') {

      const m = getMetadata();

      const sessionId = `session_${Date.now()}`;

      aiAutomationApi.post('/sessions', {

        session_id: sessionId,

        cid: String(m?.cid || ''),

        user_id: String(m?.uid || m?.userId || m?.id || ''),

        status: 'active',

      }).then(async (res) => {

        const newSessionId = String(res?.session_id || sessionId);

        queueDashboardInitialQuery(
          newSessionId,
          "Let's create a JD. Please share the job title, location, experience, skills, and qualification details.",
          { localOnly: true }
        );
        aiAutomationApi.patch(`/sessions/${newSessionId}/title`, { title: 'New Job Posting' }).catch(() => {});

        

        navigate(`/chat?session_id=${newSessionId}&from_dashboard=true`);

      }).catch(err => {

        console.error('Failed to create session:', err);

      });

    } else {

      navigate(`/chat?session_id=${sid}&from_dashboard=true`);

    }

  }, [navigate]);



  /* candidate inline card on left chat area */

  const showCandidateInChat = useCallback(c => {

    setMessages(p => [...p, { id:Date.now()+Math.random(), from:"bot", type:"candidate-card", candidate:c, ts:new Date() }]);

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior:"smooth" }), 100);

  }, []);



  /* resume inline card on left chat area */

  const showResumeInChat = useCallback(r => {

    setMessages(p => [...p, { id:Date.now()+Math.random(), from:"bot", type:"resume-card", resume:r, ts:new Date() }]);

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior:"smooth" }), 100);

  }, []);



  /* interview inline card on left chat area */

  const showInterviewInChat = useCallback(iv => {

    setMessages(p => [...p, { id:Date.now()+Math.random(), from:"bot", type:"interview-card", interview:iv, ts:new Date() }]);

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior:"smooth" }), 100);

  }, []);



  /* reschedule inline card on left chat area */

  const showRescheduleInChat = useCallback(r => {

    setMessages(p => [...p, { id:Date.now()+Math.random(), from:"bot", type:"reschedule-card", reschedule:r, ts:new Date() }]);

    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior:"smooth" }), 100);

  }, []);



  /* delete chat handler */

  const handleDeleteChat = useCallback(() => {

    setMessages(prev => {

      const preserved = prev.filter(m => 

        m.type === "onboarding" || (m.text && m.text.includes("Resumes ready"))

      );

      return preserved.length > 0 ? preserved : [{ id:1, from:"bot", type:"onboarding", ts:new Date() }];

    });

    if (user?.id) {

      localStorage.setItem(CHAT_KEY(user.id), JSON.stringify([]));

    }

    setShowDeleteConfirm(false);

  }, [user?.id]);



  const handleSend = useCallback(() => {

    const t = input.trim(); if (!t) return;

    setMessages(p => [...p, { id:Date.now(), from:"user", type:"text", text:t, ts:new Date() }]);

    setInput("");

    if (inputRef.current) inputRef.current.style.height = "46px";

    const lo = t.toLowerCase();

    if      (lo.includes("resume")||lo.includes("upload"))      openPanel(P.RESUME_UPLOAD);

    else if (lo.includes("master")||lo.includes("database"))    openPanel(P.MASTER_DATABASE);

    else if (lo.includes("candidate")||lo.includes("profile"))  openPanel(P.SHOW_CANDIDATES);

    else if (lo.includes("interview"))                          openPanel(P.MY_INTERVIEWS);

    else if (lo.includes("note")||lo.includes("transcript"))    openPanel(P.MEETING_NOTES);

    else if (lo.includes("reschedule"))                         openPanel(P.RESCHEDULE);

    else if (lo.includes("notif"))                              openPanel(P.NOTIFICATIONS);

    else if (lo.includes("today")||lo.includes("task"))         openPanel(P.TODAY_TASKS);

    else if (lo.includes("campaign")||lo.includes("pipeline"))   openPanel(P.MANAGE_CAMPAIGN);

    else if (lo.includes("close")||lo.includes("hide"))         { setPanelOpen(false); setThinking(true); setTimeout(()=>{ setThinking(false); addMsg("bot","Panel closed. What would you like to do next?",[{ icon:"👥", label:"Show Candidates", action:P.SHOW_CANDIDATES },{ icon:"✅", label:"Today's Tasks", action:P.TODAY_TASKS }]); },300); }

    else {

      setThinking(true);

      setTimeout(() => {

        setThinking(false);

        addMsg("bot","Here are some things you can do:",[

          { icon:"📄", label:"Upload Resume",    action:P.RESUME_UPLOAD },

          { icon:"👥", label:"Show Candidates",  action:P.SHOW_CANDIDATES },

          { icon:"📅", label:"My Interviews",    action:P.MY_INTERVIEWS },

        ]);

      }, 380);

    }

  }, [input, openPanel, addMsg]);



  const userInitials = useMemo(() => (user?.name||"U").split(" ").map(w=>w[0]).slice(0,2).join("").toUpperCase(), [user]);



  if (isCheckingAuth || !statsLoaded) {

    return (

      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:"system-ui,-apple-system,sans-serif", opacity:0.6 }}>

        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14 }}>

          <svg style={{ animation:"spin 1s linear infinite", width:32, height:32 }} viewBox="0 0 24 24" fill="none">

            <circle cx="12" cy="12" r="10" stroke={C.blue} strokeWidth="3" strokeDasharray="31 63" />

          </svg>

          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

          <p style={{ color:C.muted, fontSize:13 }}>{isCheckingAuth ? 'Authenticating…' : 'Loading dashboard…'}</p>

        </div>

      </div>

    );

  }



  return (

    <div className={isDark ? 'dashboard-theme-dark' : 'dashboard-theme-light'} style={{
      '--dashboard-bg': isDark ? '#0B1120' : '#F8FAFC',
      '--dashboard-surface': isDark ? '#172033' : '#F8FAFC',
      '--dashboard-card': isDark ? '#111827' : '#FFFFFF',
      '--dashboard-border': isDark ? '#334155' : '#E2E8F0',
      '--dashboard-muted': isDark ? '#94A3B8' : '#64748B',
      '--dashboard-text': isDark ? '#F8FAFC' : '#0F172A',
      '--dashboard-hover': isDark ? '#202C44' : '#EEF2FF',
      '--dashboard-blue': isDark ? '#93A4FF' : '#3B5BDB',
      '--dashboard-blue-soft': isDark ? '#20295A' : '#EEF2FF',
      '--dashboard-blue-border': isDark ? '#4657A3' : '#BFCBFF',
      '--dashboard-green': isDark ? '#34D399' : '#059669',
      '--dashboard-green-soft': isDark ? '#123C35' : '#ECFDF5',
      '--dashboard-green-border': isDark ? '#246B5B' : '#A7F3D0',
      '--dashboard-amber': isDark ? '#FBBF24' : '#D97706',
      '--dashboard-amber-soft': isDark ? '#44331A' : '#FFFBEB',
      '--dashboard-amber-border': isDark ? '#785A24' : '#FDE68A',
      '--dashboard-red': isDark ? '#F87171' : '#DC2626',
      '--dashboard-red-soft': isDark ? '#482328' : '#FEF2F2',
      '--dashboard-red-border': isDark ? '#854049' : '#F7C1C1',
      '--dashboard-input-bg': isDark ? '#0F172A' : '#FFFFFF',
      '--dashboard-input-shadow': isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'inset 0 1px 2px rgba(15,23,42,0.04)',
      '--dashboard-disabled-bg': isDark ? '#293449' : '#94A3B8',
      '--dashboard-disabled-icon': isDark ? '#94A3B8' : '#FFFFFF',
      '--dashboard-onboarding-header': isDark ? 'linear-gradient(135deg, #172033 0%, #111827 100%)' : 'linear-gradient(135deg, #EFF6FF 0%, #FFFFFF 100%)',
      '--dashboard-panel-gradient': isDark ? 'linear-gradient(180deg, #111827 0%, #0F172A 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #F5F8FC 100%)',
      '--dashboard-header-gradient': isDark ? 'linear-gradient(180deg, #172033 0%, #111827 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%)',
      '--dashboard-content-gradient': isDark ? 'linear-gradient(180deg, #111827 0%, #0B1120 100%)' : 'linear-gradient(180deg, #F8FAFD 0%, #F3F7FC 100%)',
      '--dashboard-modal-bg': isDark ? 'linear-gradient(180deg, #182237 0%, #0F172A 100%)' : '#FFFFFF',
      '--dashboard-modal-border': isDark ? '#46566F' : '#E2E8F0',
      '--dashboard-modal-shadow': isDark ? '0 28px 70px rgba(0,0,0,0.58)' : '0 24px 60px rgba(15,23,42,0.18)',
      fontFamily:"system-ui,-apple-system,sans-serif",
      background: isDark ? 'linear-gradient(180deg, #0B1120 0%, #0F172A 100%)' : 'radial-gradient(circle at top left, rgba(37,99,235,0.08), transparent 24%), radial-gradient(circle at top right, rgba(16,185,129,0.06), transparent 20%), linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)',
      color:C.text, height:"100vh", minHeight:"100vh", display:"flex", flexDirection:"column"
    }}>

      <AppHeader />



      <div ref={containerRef} style={{

        flex:1, display:"flex", overflow:"hidden",

        margin:"10px", borderRadius:20, border:`1px solid ${C.border}`,

        background:"var(--dashboard-card, #FFFFFF)", boxShadow:isDark?"0 18px 40px rgba(0, 0, 0, 0.28)":"0 18px 40px rgba(15, 23, 42, 0.06)",

        userSelect:isResizing?"none":"auto", cursor:isResizing?"col-resize":"auto",

        height: "calc(100vh - 56px)", 

        minHeight:0

      }}>

        {/* ── LEFT CHAT COLUMN ── */}

        <div style={{ display:"flex", flexDirection:"column", flex:1, minWidth:0, minHeight:0 }}>

          <StickyStatsBar stats={stats} onAction={openPanel} onDelete={() => setShowDeleteConfirm(true)} />



        {/* Messages */}

        <div style={{ flex:1, overflowY:"auto", padding:"0 12px 12px", display:"flex", flexDirection:"column", gap:10, background:"var(--dashboard-content-gradient, linear-gradient(180deg, #FBFDFF 0%, #F4F7FB 100%))", minHeight:0 }}>

          {messages.map(m => {

            if (m.type==="onboarding") return (

              <div key={m.id} style={{ padding:"10px 8px 0" }}>

                <OnboardingCard stats={stats} onAction={openPanel} user={user} />

              </div>

            );

            if (m.type==="candidate-card") return (

              <div key={m.id} style={{ display:"flex", gap:8, alignItems:"flex-start" }}>

                <div style={{ marginTop:1, animation:"tgptSystemGlow 2.4s ease-in-out infinite" }}>
                  <SystemAvatar />
                </div>

                <div>

                  <p style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Here are the details for <strong>{m.candidate?.name||m.candidate?.candidate_name||"Candidate"}</strong>:</p>

                  <CandidateInlineCard candidate={m.candidate} onAction={openPanel} />

                  {m.ts && <div style={{ fontSize:10, color:"#ccc", marginTop:4 }}>{fmt(m.ts)}</div>}

                </div>

              </div>

            );

            if (m.type==="resume-card") return (

              <div key={m.id} style={{ display:"flex", gap:8, alignItems:"flex-start" }}>

                <div style={{ marginTop:1, animation:"tgptSystemGlow 2.4s ease-in-out infinite" }}>
                  <SystemAvatar />
                </div>

                <div>

                  <p style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Here are the details for <strong>{m.resume?.file_name||m.resume?.name||"Resume"}</strong>:</p>

                  <ResumeInlineCard resume={m.resume} />

                  {m.ts && <div style={{ fontSize:10, color:"#ccc", marginTop:4 }}>{fmt(m.ts)}</div>}

                </div>

              </div>

            );

            if (m.type==="interview-card") return (

              <div key={m.id} style={{ display:"flex", gap:8, alignItems:"flex-start" }}>

                <div style={{ marginTop:1, animation:"tgptSystemGlow 2.4s ease-in-out infinite" }}>
                  <SystemAvatar />
                </div>

                <div>

                  <p style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Here are the details for <strong>{m.interview?.candidate_name||"Interview"}</strong>:</p>

                  <InterviewInlineCard interview={m.interview} />

                  {m.ts && <div style={{ fontSize:10, color:"#ccc", marginTop:4 }}>{fmt(m.ts)}</div>}

                </div>

              </div>

            );

            if (m.type==="reschedule-card") return (

              <div key={m.id} style={{ display:"flex", gap:8, alignItems:"flex-start" }}>

                <div style={{ marginTop:1, animation:"tgptSystemGlow 2.4s ease-in-out infinite" }}>
                  <SystemAvatar />
                </div>

                <div>

                  <p style={{ fontSize:11, color:C.muted, marginBottom:6 }}>Here are the details for <strong>{m.reschedule?.candidate_name||m.reschedule?.candidate_email||"Reschedule Request"}</strong>:</p>

                  <RescheduleInlineCard reschedule={m.reschedule} />

                  {m.ts && <div style={{ fontSize:10, color:"#ccc", marginTop:4 }}>{fmt(m.ts)}</div>}

                </div>

              </div>

            );

            if (m.from==="user") return <UserBubble key={m.id} text={m.text} ts={m.ts} initials={userInitials} />;

            return <BotBubble key={m.id} text={m.text} actions={m.actions||[]} onAction={openPanel} ts={m.ts} />;

          })}

          {thinking && <ThinkingDots />}

          <div ref={chatEndRef} />

        </div>



        {/* Quick-action chips */}

        <div style={{ padding:"10px 12px", display:"flex", flexWrap:"wrap", gap:6, borderTop:`1px solid ${C.border}` , background:'var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%))' }}>

          {(stats.resumes === 0 && stats.campaigns === 0 ? [

            { id: 'create_jd', label: "Create JD" },

            { id: P.RESUME_UPLOAD, label: "+ Resume Upload" },

            { id: P.TODAY_TASKS, label: "Today's Tasks" }

          ] : QUICK_ACTIONS).filter((a) => {
            const requiredFeature = PANEL_FEATURE_ACCESS[a.id];
            return !requiredFeature || canUseFeature(requiredFeature);
          }).map(a => {

            const active = panelOpen && panel===a.id;

            return (

              <button key={a.id} onClick={() => openPanel(a.id)} style={{

                borderRadius:13, padding:"5px 11px", fontSize:11, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap",

                background:active ? "linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)" : "var(--dashboard-card, #FFFFFF)", color:active?"#fff":C.text,

                border:`1px solid ${active ? '#3B5BDB' : C.border}`,
                boxShadow: active ? "0 10px 20px rgba(37, 99, 235, 0.16)" : "0 6px 14px rgba(15, 23, 42, 0.04)",

                transition:"all .12s"

              }}>{a.label}</button>

            );

          })}

        </div>



        {/* Session Popup for Create JD */}

        <SessionPopup

          open={showSessionPopup}

          onClose={()=>setShowSessionPopup(false)}

          onSelect={handleSessionSelect}

        />



        {/* Delete Chat Confirmation Popup */}

        {showDeleteConfirm && (

          <div style={{ position:"fixed", inset:0, zIndex:1000, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center" }}>

            <div style={{ background:"var(--dashboard-card, #fff)", borderRadius:14, padding:28, minWidth:340, maxWidth:400, boxShadow:"0 8px 32px rgba(0,0,0,0.12)" }}>

              <h3 style={{ fontSize:18, fontWeight:700, marginBottom:8 }}>Delete Chat?</h3>

              <p style={{ fontSize:13, color:C.muted, marginBottom:18 }}>This will clear all chat messages. This action cannot be undone.</p>

              <div style={{ display:"flex", gap:10 }}>

                <button onClick={handleDeleteChat} style={{ flex:1, padding:"10px 0", borderRadius:8, background:"#EF4444", color:"#fff", fontWeight:600, cursor:"pointer", border:"none" }}>Delete</button>

                <button onClick={() => setShowDeleteConfirm(false)} style={{ flex:1, padding:"10px 0", borderRadius:8, background:"var(--dashboard-surface, #f7f8ff)", color:C.text, fontWeight:500, cursor:"pointer", border:`1px solid ${C.border}` }}>Cancel</button>

              </div>

            </div>

          </div>

        )}



        {/* Input */}

        <div style={{ padding:"12px 14px 12px", borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"flex-end", gap:8, background:"var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))" }}>

          <textarea ref={inputRef} rows={1} value={input}

            onChange={e => { setInput(e.target.value); e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}

            onKeyDown={e => { if (e.key==="Enter"&&!e.shiftKey) { e.preventDefault(); handleSend(); } }}

            placeholder="Type a message or command…"

            style={{ flex:1, resize:"none", borderRadius:20, border:`1px solid ${C.border}`, background:"var(--dashboard-input-bg, var(--dashboard-card, #FFFFFF))", padding:"10px 16px", fontSize:13, outline:"none", color:C.text, minHeight:46, maxHeight:120, lineHeight:1.5, fontFamily:"inherit", boxShadow:"var(--dashboard-input-shadow, inset 0 1px 2px rgba(15, 23, 42, 0.04))" }} />

          <button onClick={handleSend} disabled={!input.trim()} style={{ width:38, height:38, borderRadius:"50%", background:input.trim()?"linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)":"var(--dashboard-disabled-bg, #D7E0EE)", border:`1px solid ${input.trim() ? 'transparent' : C.border}`, cursor:input.trim()?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background .12s, border-color .12s, box-shadow .12s", boxShadow: input.trim() ? "0 10px 20px rgba(59, 91, 219, 0.16)" : "inset 0 1px 0 rgba(255,255,255,0.05)" }}>

            <svg width="13" height="13" viewBox="0 0 24 24" fill={input.trim() ? "#fff" : "var(--dashboard-disabled-icon, #fff)"}><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>

          </button>

        </div>

        <div style={{ fontSize:10, color:C.muted, textAlign:"center", padding:"0 14px 10px", background:"var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))" }}>Enter to send · Shift+Enter for new line</div>

      </div>



      {/* ── RESIZE HANDLE ── */}

      {panelOpen && (

        <div onMouseDown={startResize} title="Drag to resize" style={{

          width:5, flexShrink:0, cursor:"col-resize",

          background:isResizing?C.blueMid:"transparent",

          display:"flex", alignItems:"center", justifyContent:"center", transition:"background .15s",

        }}>

          <div style={{ width:2, height:36, borderRadius:2, background:C.border }} />

        </div>

      )}



      {/* ── RIGHT PANEL ── */}

    <RightPanel

      open={panelOpen} width={panelWidth} panel={panel}

      user={user}

      onClose={() => { setPanelOpen(false); }}

      onSelectCandidate={c => showCandidateInChat(c)}

      onShowResume={r => showResumeInChat(r)}

      onShowInterview={iv => showInterviewInChat(iv)}

      onShowReschedule={r => showRescheduleInChat(r)}

      onSwitchPanel={id => { setPanel(id); }}

      onSelectChatSession={id => { setSelectedChatSessionId(id); }}

      selectedChatSessionId={selectedChatSessionId}

      uploadFileLogs={uploadFileLogs}

      setUploadFileLogs={setUploadFileLogs}

      isParsingActive={isParsingActive}

      setIsParsingActive={setIsParsingActive}
      setShowSessionPopup={setShowSessionPopup}
      onRefreshStats={loadDashboardStats}

      />

    </div>

  </div>

);

}
